import React, { useEffect, useMemo, useRef, useState } from 'react';
import PePapersDisclaimerGate from '../pe-papers/shared/PePapersDisclaimerGate';
import { createPortal } from 'react-dom';
import { Lock, RotateCcw, X } from 'lucide-react';
import { savePePapersArchiveDoc } from '../../services/pePapersArchiveService';
import useAppStore from '../../store/appStore';
import { usePersonalDataSelector } from '../../store/usePersonalDataSelector';
import {
    calculateBruttoFromStundenlohn,
    calculateStundenlohnFromBrutto,
    calculateWochenstundenFromPlan,
    createArbeitszeitEntryFromPrevious,
    formatStdForDisplay,
    isArbeitszeitEntryComplete,
    sortArbeitsplanByWochentag,
    type ArbeitszeitTagEntry,
} from '../../utils/pe-papers/arbeitszeitPlan';
import { downloadHtmlAsPdf } from '../../utils/pe-papers/htmlDocumentPdfDownload';
import { SondervereinbarungenField, renderSondervereinbarungenInDocument } from '../pe-papers/shared/SondervereinbarungenField';
import ArbeitszeitPlanDocumentTable from '../pe-papers/shared/ArbeitszeitPlanDocumentTable';
import ArbeitszeitPlanEditor from '../pe-papers/shared/ArbeitszeitPlanEditor';

/** Noble Slate — normale Lesbarkeit, nur leicht enger als AV (1× A4) */
const VA_CONTRACT_PDF_CSS = `
  .av-a4-page {
    display: block !important;
    width: 100% !important;
    max-width: 210mm !important;
    margin-left: auto !important;
    margin-right: auto !important;
    box-sizing: border-box !important;
    font-family: Georgia, 'Times New Roman', serif !important;
    color: #0f172a !important;
    font-size: 12px !important;
    line-height: 1.48 !important;
    padding: 11mm 13mm !important;
    background: #ffffff !important;
  }
  .av-a4-page * { box-sizing: border-box !important; }
  .av-a4-page .pdf-letterhead {
    width: 100% !important;
    text-align: center !important;
    border-bottom: 2px solid #1e293b !important;
    margin: 0 0 12px !important;
    padding: 0 0 10px !important;
  }
  .av-a4-page .pdf-letterhead h1 {
    font-family: Arial, Helvetica, sans-serif !important;
    font-size: 20px !important;
    font-weight: 900 !important;
    letter-spacing: 0.12em !important;
    text-transform: uppercase !important;
    color: #0f172a !important;
    background: transparent !important;
    border: none !important;
    padding: 0 !important;
    margin: 0 0 3px !important;
    text-align: center !important;
  }
  .av-a4-page .pdf-letterhead h2 {
    font-family: Arial, Helvetica, sans-serif !important;
    font-size: 10.5px !important;
    font-weight: 700 !important;
    letter-spacing: 0.16em !important;
    text-transform: uppercase !important;
    color: #b48a56 !important;
    background: transparent !important;
    border: none !important;
    padding: 0 !important;
    margin: 0 !important;
    text-align: center !important;
  }
  .av-meta-grid {
    display: table !important;
    width: 100% !important;
    table-layout: fixed !important;
    margin: 0 0 12px !important;
    border: 1px solid #cbd5e1 !important;
    background: #f8fafc !important;
  }
  .av-meta-col {
    display: table-cell !important;
    width: 50% !important;
    vertical-align: top !important;
    padding: 10px 12px !important;
    font-size: 12px !important;
    line-height: 1.4 !important;
    color: #334155 !important;
    text-align: left !important;
  }
  .av-meta-col + .av-meta-col { border-left: 1px solid #e2e8f0 !important; }
  .av-meta-label {
    display: block !important;
    font-family: Arial, Helvetica, sans-serif !important;
    font-size: 11px !important;
    font-weight: 700 !important;
    text-transform: uppercase !important;
    letter-spacing: 0.12em !important;
    color: #64748b !important;
    margin: 0 0 4px !important;
  }
  .av-meta-col strong { color: #0f172a !important; font-weight: 700 !important; }
  .av-meta-col p {
    width: 100% !important;
    margin: 0 !important;
    text-align: left !important;
    font-size: 12px !important;
    line-height: 1.4 !important;
    color: #334155 !important;
  }
  .av-meta-col .av-meta-date {
    display: block !important;
    margin-top: 4px !important;
    color: #64748b !important;
    font-size: 11px !important;
  }
  .av-doc-section {
    display: block !important;
    width: 100% !important;
    margin: 0 0 12px !important;
  }
  .av-a4-page h3,
  .av-doc-section > h3 {
    display: block !important;
    width: 100% !important;
    font-family: Arial, Helvetica, sans-serif !important;
    font-size: 11.5px !important;
    font-weight: 700 !important;
    text-transform: uppercase !important;
    letter-spacing: 0.05em !important;
    text-align: left !important;
    background: #1e293b !important;
    color: #f8fafc !important;
    border: none !important;
    border-radius: 0 !important;
    padding: 6px 10px !important;
    margin: 0 0 8px !important;
  }
  .av-a4-page p,
  .av-doc-p,
  .av-doc-section p {
    display: block !important;
    width: 100% !important;
    max-width: 100% !important;
    font-family: Georgia, 'Times New Roman', serif !important;
    font-size: 12px !important;
    line-height: 1.48 !important;
    color: #1e293b !important;
    text-align: justify !important;
    margin: 0 0 8px !important;
  }
  .av-table-wrap {
    display: block !important;
    width: 100% !important;
    margin: 0 0 10px !important;
  }
  .av-table-wrap > p {
    text-align: left !important;
    font-family: Arial, Helvetica, sans-serif !important;
    font-size: 10.5px !important;
    font-weight: 700 !important;
    text-transform: uppercase !important;
    letter-spacing: 0.07em !important;
    color: #64748b !important;
    margin: 0 0 5px !important;
  }
  .av-table-wrap table {
    width: 100% !important;
    border-collapse: collapse !important;
    table-layout: fixed !important;
    font-size: 12px !important;
  }
  .av-table-wrap thead th,
  .av-table-wrap th {
    background-color: #1e293b !important;
    color: #f8fafc !important;
    font-family: Arial, Helvetica, sans-serif !important;
    font-size: 11px !important;
    font-weight: 600 !important;
    text-transform: uppercase !important;
    letter-spacing: 0.05em !important;
    padding: 5px 7px !important;
    border: 1px solid #334155 !important;
    text-align: left !important;
  }
  .av-table-wrap td {
    padding: 5px 7px !important;
    border: 1px solid #e2e8f0 !important;
    color: #334155 !important;
    text-align: left !important;
    font-size: 12px !important;
    line-height: 1.35 !important;
  }
  .av-table-wrap tfoot td {
    background: #f8fafc !important;
    font-family: Arial, Helvetica, sans-serif !important;
    font-weight: 700 !important;
    color: #0f172a !important;
    border-top: 1px solid #cbd5e1 !important;
    padding: 5px 7px !important;
    font-size: 12px !important;
  }
  .av-table-wrap tfoot td.va-tfoot-label {
    text-align: right !important;
    font-weight: 600 !important;
  }
  .av-table-wrap tfoot td.va-tfoot-value {
    text-align: right !important;
    font-weight: 700 !important;
  }
  .va-sig-table {
    width: 100% !important;
    border-collapse: collapse !important;
    table-layout: fixed !important;
    margin-top: 32px !important;
    border: none !important;
  }
  .va-sig-table td {
    width: 50% !important;
    vertical-align: top !important;
    border: none !important;
    background: transparent !important;
    padding: 0 !important;
  }
  .va-sig-table p {
    text-align: left !important;
    margin: 0 !important;
    width: auto !important;
    max-width: 100% !important;
    font-family: Arial, Helvetica, sans-serif !important;
  }
  .va-sig-line-soft {
    display: block !important;
    width: 200px !important;
    max-width: 100% !important;
    border: none !important;
    border-bottom: 2px solid #1e293b !important;
    margin-top: 28px !important;
    margin-bottom: 6px !important;
    height: 0 !important;
    padding: 0 !important;
    background: transparent !important;
  }
  .av-summary-box {
    display: block !important;
    width: 100% !important;
    border: 1px solid #cbd5e1 !important;
    background: #f8fafc !important;
    padding: 8px 10px !important;
    margin: 0 0 10px !important;
    font-family: Arial, Helvetica, sans-serif !important;
    font-size: 12px !important;
    line-height: 1.4 !important;
    color: #334155 !important;
  }
  .av-summary-box strong { color: #0f172a !important; }
  .print-signatures-grid {
    display: table !important;
    width: 100% !important;
    table-layout: fixed !important;
    margin-top: 22px !important;
  }
  .print-signatures-grid .signature-col {
    display: table-cell !important;
    width: 50% !important;
    padding-right: 16px !important;
    vertical-align: top !important;
  }
  .print-signatures-grid .signature-col:last-child {
    padding-right: 0 !important;
    padding-left: 16px !important;
  }
  .av-sig-line {
    border-top: 1px solid #1e293b !important;
    padding-top: 5px !important;
    margin-top: 28px !important;
    font-family: Arial, Helvetica, sans-serif !important;
    font-size: 11.5px !important;
    color: #475569 !important;
    text-align: left !important;
  }
  .av-sig-name {
    font-family: Arial, Helvetica, sans-serif !important;
    font-size: 11px !important;
    color: #64748b !important;
    margin-top: 3px !important;
  }
  @media print {
    @page { size: A4; margin: 0; }
    body { margin: 0 !important; padding: 0 !important; background: #fff !important; }
    .av-a4-page {
      box-shadow: none !important;
      margin: 0 !important;
      max-width: none !important;
      width: 100% !important;
      min-height: 0 !important;
    }
  }
`;

const VA_PRINT_CSS = `
${VA_CONTRACT_PDF_CSS}
html, body {
  margin: 0 !important;
  padding: 0 !important;
  background: #fff !important;
}
`;

interface VertragsAnpassungKombiModalProps {
    isOpen: boolean;
    onClose: () => void;
    initialData?: any;
    onArchived?: () => void;
}

type Vertragstyp = 'aenderungsvertrag' | 'aenderungskuendigung';

interface BetriebFormData {
    firmenname: string;
    strasse: string;
    plz: string;
    ort: string;
}

interface MitarbeiterFormData {
    name: string;
    strasse: string;
    plz: string;
    ort: string;
}

interface BetriebFieldLocks {
    firmenname: boolean;
    strasse: boolean;
    plz: boolean;
    ort: boolean;
}

type AenderungsBereichKey = 'arbeitszeit' | 'verguetung' | 'filiale' | 'sonstiges';

interface AenderungsBereiche {
    arbeitszeit: boolean;
    verguetung: boolean;
    filiale: boolean;
    sonstiges: boolean;
}

const INITIAL_AENDERUNGS_BEREICHE: AenderungsBereiche = {
    arbeitszeit: false,
    verguetung: false,
    filiale: false,
    sonstiges: false,
};

const INITIAL_MITARBEITER_DATA: MitarbeiterFormData = {
    name: '',
    strasse: '',
    plz: '',
    ort: '',
};

const VERTRAGSTYP_OPTIONS: { key: Vertragstyp; label: string }[] = [
    { key: 'aenderungsvertrag', label: 'Änderungsvertrag' },
    { key: 'aenderungskuendigung', label: 'Änderungskündigung' },
];

const AENDERUNGS_BEREICH_OPTIONS: { key: AenderungsBereichKey; label: string }[] = [
    { key: 'arbeitszeit', label: 'Arbeitszeit' },
    { key: 'verguetung', label: 'Vergütung / Provisionsmodell' },
    { key: 'filiale', label: 'Arbeitsort / Filialversetzung' },
    { key: 'sonstiges', label: 'Sonstiges' },
];

const hasAnyAenderungsBereich = (bereiche: AenderungsBereiche): boolean =>
    bereiche.arbeitszeit || bereiche.verguetung || bereiche.filiale || bereiche.sonstiges;

const A4_PREVIEW_PAGE_CLASS =
    'av-a4-page bg-white text-slate-900 my-4 mx-auto w-full max-w-[210mm] h-auto min-h-0 shadow-xl rounded-sm text-left block overflow-visible print:my-0 print:shadow-none';

const DOC_P_CLASS = 'av-doc-p mb-2 text-justify text-slate-800 leading-relaxed';

const INPUT_CLASS =
    'w-full rounded-lg border border-pe-border dark:border-slate-700 bg-pe-input dark:bg-black text-pe-text dark:text-white px-4 py-2.5 text-sm focus:outline-none focus:border-[#E6C299]/50 transition-colors';

const DATE_INPUT_CLASS = `${INPUT_CLASS} cursor-pointer kombi-date-input`;

const LABEL_CLASS = 'text-xs font-semibold text-pe-muted dark:text-slate-400 uppercase tracking-wider mb-1 block';

const READONLY_INPUT_CLASS =
    'w-full rounded-lg border border-pe-border dark:border-slate-700 bg-pe-elevated dark:bg-[#0a0a0a] text-pe-accent-text dark:text-[#E6C299] px-4 py-2.5 text-sm cursor-not-allowed opacity-90';

const ERROR_INPUT_RING = 'border-red-500 focus:border-red-500 focus:ring-1 focus:ring-red-500/20';

const STEP_LABELS: Record<number, string> = {
    1: 'Datenerfassung & Typ-Weiche',
    2: 'Vorschau & Export',
};

const BESTANDSSCHUTZ_KLAUSEL =
    'Alle übrigen Vereinbarungen des Hauptarbeitsvertrages bleiben unberührt und behalten vollumfänglich ihre Gültigkeit.';

const KSchG_HINWEIS =
    'Der Arbeitnehmer wird ausdrücklich daraufhin gewiesen, dass er gemäß § 2 KSchG innerhalb von drei Wochen nach Zugang dieser Änderungskündigung erklären kann, ob er das Arbeitsverhältnis unter den bisherigen oder den angebotenen geänderten Bedingungen fortsetzen möchte. Die Frist beginnt mit Zugang dieses Schreibens.';

type GehaltMode = 'brutto' | 'stundenlohn';

const parseNum = (value: string): number => {
    const s = String(value || '').trim();
    if (!s) return 0;
    // number-input / ASCII: "8.5" oder "32" — Punkte nicht als Tausender werten
    if (/^\d+(\.\d+)?$/.test(s)) return Number(s);
    // de-DE: "1.234,56" oder "8,5"
    const normalized = s.replace(/\./g, '').replace(',', '.');
    const num = Number(normalized);
    return Number.isFinite(num) ? num : 0;
};

/** Für <input type="number"> — Komma kaputtmacht den Browser-Wert (leeres Feld). */
const formatStdForNumberInput = (value: number): string => {
    if (!Number.isFinite(value) || value <= 0) return '';
    const rounded = Math.round(value * 100) / 100;
    if (Number.isInteger(rounded)) return String(rounded);
    return String(rounded);
};

const isPositiveNum = (value: string): boolean => parseNum(value) > 0;

const formatNumInputDe = (value: number, fractionDigits = 2): string => {
    if (!Number.isFinite(value) || value <= 0) return '';
    return value.toLocaleString('de-DE', {
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
    });
};

interface StammdatenMitarbeiterOption {
    id: string;
    name: string;
    group: string;
    wochenstunden: number;
    bruttolohnGesamt: number;
    stundenlohnGesamt: number;
    adresse: string;
}

const formatTodayGerman = (): string => {
    const now = new Date();
    const dd = String(now.getDate()).padStart(2, '0');
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const yyyy = now.getFullYear();
    return `${dd}.${mm}.${yyyy}`;
};

const formatDisplayDate = (value: string): string => {
    if (!value.trim()) return '____________';
    const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (isoMatch) return `${isoMatch[3]}.${isoMatch[2]}.${isoMatch[1]}`;
    return value.trim();
};

const formatStdForDocument = (value: string): string => {
    const normalized = String(value || '').trim().replace(',', '.');
    const num = Number(normalized);
    if (!Number.isFinite(num) || num <= 0) return value.trim();
    if (Number.isInteger(num)) return String(num);
    return num.toFixed(1).replace('.', ',');
};

const isEmpty = (value: string) => !String(value || '').trim();

/** Legacy-Archiv / Stammdaten: einzeilige `adresse` → Straße / PLZ / Ort */
const parseLegacyMitarbeiterAdresse = (
    adresse: string
): Pick<MitarbeiterFormData, 'strasse' | 'plz' | 'ort'> => {
    const raw = String(adresse || '').trim();
    if (!raw) return { strasse: '', plz: '', ort: '' };

    const lines = raw
        .split(/[\n;]+/)
        .map((s) => s.trim())
        .filter(Boolean);

    const plzOrtMatch = (value: string) => {
        const m = value.match(/^(\d{4,5})\s+(.+)$/);
        return m ? { plz: m[1], ort: m[2].trim() } : null;
    };

    if (lines.length === 1) {
        const parts = lines[0]
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean);
        if (parts.length >= 2) {
            const last = plzOrtMatch(parts[parts.length - 1]);
            if (last) {
                return {
                    strasse: parts.slice(0, -1).join(', '),
                    plz: last.plz,
                    ort: last.ort,
                };
            }
        }
        const single = plzOrtMatch(lines[0]);
        if (single) return { strasse: '', ...single };
        return { strasse: lines[0], plz: '', ort: '' };
    }

    const lastLine = plzOrtMatch(lines[lines.length - 1]);
    if (lastLine) {
        return {
            strasse: lines.slice(0, -1).join(', '),
            plz: lastLine.plz,
            ort: lastLine.ort,
        };
    }
    return { strasse: lines[0], plz: '', ort: lines.slice(1).join(' ') };
};

const normalizeMitarbeiterData = (incoming: any): MitarbeiterFormData => {
    const src = incoming && typeof incoming === 'object' ? incoming : {};
    const hasStructured =
        !isEmpty(String(src.strasse || '')) ||
        !isEmpty(String(src.plz || '')) ||
        !isEmpty(String(src.ort || ''));
    if (!hasStructured && typeof src.adresse === 'string' && src.adresse.trim()) {
        const parsed = parseLegacyMitarbeiterAdresse(src.adresse);
        return {
            name: String(src.name || ''),
            strasse: parsed.strasse,
            plz: parsed.plz,
            ort: parsed.ort,
        };
    }
    return {
        name: String(src.name || ''),
        strasse: String(src.strasse || ''),
        plz: String(src.plz || ''),
        ort: String(src.ort || ''),
    };
};

const formatMitarbeiterAdresseInline = (data: MitarbeiterFormData): string => {
    const line1 = data.strasse.trim();
    const line2 = [data.plz.trim(), data.ort.trim()].filter(Boolean).join(' ');
    return [line1, line2].filter(Boolean).join(', ');
};

const formatGehaltEuro = (value: number): string => {
    if (!Number.isFinite(value) || value <= 0) return '____________';
    return `${value.toFixed(2).replace('.', ',')} € brutto`;
};

const formatStundenlohnForDocument = (value: number): string => {
    if (!Number.isFinite(value) || value <= 0) return '____________';
    return `${value.toFixed(2).replace('.', ',')} € brutto / Std`;
};

interface BgbFristBerechnung {
    beschaeftigungsjahre: number;
    defaultKuendigungsfristText: string;
    infoFristAnzeige: string;
}

const parseIsoDate = (iso: string): Date | null => {
    const match = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) return null;
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
};

const calculateBeschaeftigungsjahre = (startIso: string, referenceDate: Date = new Date()): number => {
    const start = parseIsoDate(startIso);
    if (!start) return 0;

    let years = referenceDate.getFullYear() - start.getFullYear();
    const monthDiff = referenceDate.getMonth() - start.getMonth();
    const dayDiff = referenceDate.getDate() - start.getDate();
    if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
        years -= 1;
    }

    return Math.max(0, years);
};

const calculateBgbKuendigungsfrist = (beschaeftigungsjahre: number): BgbFristBerechnung => {
    if (beschaeftigungsjahre < 2) {
        return {
            beschaeftigungsjahre,
            defaultKuendigungsfristText: '4 Wochen',
            infoFristAnzeige: '4 Wochen',
        };
    }

    let monate = 1;
    if (beschaeftigungsjahre >= 20) monate = 7;
    else if (beschaeftigungsjahre >= 15) monate = 6;
    else if (beschaeftigungsjahre >= 12) monate = 5;
    else if (beschaeftigungsjahre >= 10) monate = 4;
    else if (beschaeftigungsjahre >= 8) monate = 3;
    else if (beschaeftigungsjahre >= 5) monate = 2;

    const defaultText = monate === 1 ? '1 Monat' : `${monate} Monaten`;
    const infoAnzeige = monate === 1 ? '1 Monat' : `${monate} Monate`;

    return {
        beschaeftigungsjahre,
        defaultKuendigungsfristText: defaultText,
        infoFristAnzeige: infoAnzeige,
    };
};

const buildBgbFristInfoText = (berechnung: BgbFristBerechnung): string => {
    const jahreLabel =
        berechnung.beschaeftigungsjahre === 1
            ? '1 Beschäftigungsjahr'
            : `${berechnung.beschaeftigungsjahre} Beschäftigungsjahre`;

    return `ℹ️ Rechtlicher Fristen-Check: Nach § 622 BGB (gesetzlicher Standard) beträgt die Mindestfrist für ${jahreLabel} aktuell ${berechnung.infoFristAnzeige} zum Monatsende. Achtung: Tarifliche oder abweichende vertragliche Vereinbarungen des jeweiligen Bundeslandes können vorgehen!`;
};

const buildAenderungsTexte = (
    vertragstyp: Vertragstyp,
    bereiche: AenderungsBereiche,
    inkrafttretenDatum: string,
    alteArbeitszeitStd: string,
    neueArbeitszeitStd: string,
    neuerArbeitsplan: ArbeitszeitTagEntry[],
    altEffektivBrutto: number,
    neuEffektivBrutto: number,
    altEffektivStundenlohn: number,
    neuEffektivStundenlohn: number,
    bisherigeFiliale: string,
    neueFiliale: string,
    sonstigesFreitext: string
): string[] => {
    if (!hasAnyAenderungsBereich(bereiche)) return [];

    const inkrafttretenText = formatDisplayDate(inkrafttretenDatum);
    const isKuendigung = vertragstyp === 'aenderungskuendigung';
    const texte: string[] = [];

    if (bereiche.arbeitszeit) {
        const altStd = formatStdForDocument(alteArbeitszeitStd);
        const neuStd = formatStdForDocument(neueArbeitszeitStd);
        if (isKuendigung) {
            texte.push(
                `Die im Hauptarbeitsvertrag vereinbarte regelmäßige wöchentliche Arbeitszeit wird von bisher ${altStd} Stunden auf nunmehr ${neuStd} Stunden wöchentlich angepasst.`
            );
        } else {
            texte.push(
                `Die im Hauptarbeitsvertrag vereinbarte regelmäßige wöchentliche Arbeitszeit wird einvernehmlich von bisher ${altStd} Stunden auf nunmehr ${neuStd} Stunden wöchentlich angepasst.`
            );
        }
        const gueltigePlanEintraege = sortArbeitsplanByWochentag(neuerArbeitsplan).filter(isArbeitszeitEntryComplete);
        if (gueltigePlanEintraege.length > 0) {
            texte.push(
                `Die wöchentliche Verteilung der Arbeitszeit erfolgt ab dem ${inkrafttretenText} wie in der nachfolgenden Übersicht dargestellt.`
            );
        }
    }

    if (bereiche.verguetung) {
        const altVerg = formatGehaltEuro(altEffektivBrutto);
        const neuVerg = formatGehaltEuro(neuEffektivBrutto);
        const altStdLohn = formatStundenlohnForDocument(altEffektivStundenlohn);
        const neuStdLohn = formatStundenlohnForDocument(neuEffektivStundenlohn);
        if (isKuendigung) {
            texte.push(
                `Die vertraglich vereinbarte monatliche Bruttovergütung wird von bisher ${altVerg} auf nunmehr ${neuVerg} angepasst. Der Stundenlohn ändert sich entsprechend von ${altStdLohn} auf ${neuStdLohn}.`
            );
        } else {
            texte.push(
                `Die vertraglich vereinbarte monatliche Bruttovergütung wird einvernehmlich von bisher ${altVerg} auf nunmehr ${neuVerg} angepasst. Der Stundenlohn ändert sich entsprechend von ${altStdLohn} auf ${neuStdLohn}.`
            );
        }
    }

    if (bereiche.filiale && bisherigeFiliale.trim() && neueFiliale.trim()) {
        if (isKuendigung) {
            texte.push(
                `Der Einsatzort des Arbeitnehmers ändert sich. Der Arbeitnehmer wird mit Wirkung zum ${inkrafttretenText} von der bisherigen Filiale ${bisherigeFiliale.trim()} in die Filiale ${neueFiliale.trim()} versetzt.`
            );
        } else {
            texte.push(
                `Der Einsatzort des Arbeitnehmers ändert sich einvernehmlich. Der Arbeitnehmer wird mit Wirkung zum ${inkrafttretenText} von der bisherigen Filiale ${bisherigeFiliale.trim()} in die Filiale ${neueFiliale.trim()} versetzt.`
            );
        }
    }

    if (bereiche.sonstiges && sonstigesFreitext.trim()) {
        if (isKuendigung) {
            texte.push(
                `Mit Wirkung zum ${inkrafttretenText} gilt folgende Neuregelung des Arbeitsverhältnisses: ${sonstigesFreitext.trim()}`
            );
        } else {
            texte.push(
                `Die Parteien vereinbaren mit Wirkung zum ${inkrafttretenText} einvernehmlich folgende Neuregelung des Arbeitsverhältnisses: ${sonstigesFreitext.trim()}`
            );
        }
    }

    return texte;
};

const buildBetriebFromClient = (clientData: any): { betrieb: BetriebFormData; locks: BetriebFieldLocks } => {
    const firmenname = clientData?.salonname || clientData?.firmenname || '';
    const strasse = clientData?.strasse || '';
    const plz = clientData?.plz || '';
    const ort = clientData?.ort || '';

    return {
        betrieb: { firmenname, strasse, plz, ort },
        locks: {
            firmenname: !isEmpty(firmenname),
            strasse: !isEmpty(strasse),
            plz: !isEmpty(plz),
            ort: !isEmpty(ort),
        },
    };
};

const VertragsAnpassungKombiModal: React.FC<VertragsAnpassungKombiModalProps> = ({
    isOpen,
    onClose,
    initialData,
    onArchived,
}) => {
    const [currentStep, setCurrentStep] = useState(1);
    const [vertragstyp, setVertragstyp] = useState<Vertragstyp>('aenderungsvertrag');
    const [hauptvertragDatum, setHauptvertragDatum] = useState('');
    const [inkrafttretenDatum, setInkrafttretenDatum] = useState('');
    const [aenderungsBereiche, setAenderungsBereiche] = useState<AenderungsBereiche>(INITIAL_AENDERUNGS_BEREICHE);
    const [sonstigesFreitext, setSonstigesFreitext] = useState('');
    const [sondervereinbarungen, setSondervereinbarungen] = useState('');
    const [alteArbeitszeitStd, setAlteArbeitszeitStd] = useState('');
    const [neueArbeitszeitStd, setNeueArbeitszeitStd] = useState('');
    const [alterArbeitsplan, setAlterArbeitsplan] = useState<ArbeitszeitTagEntry[]>([]);
    const [neuerArbeitsplan, setNeuerArbeitsplan] = useState<ArbeitszeitTagEntry[]>([createArbeitszeitEntryFromPrevious()]);
    const [alteVerguetung, setAlteVerguetung] = useState('');
    const [neueVerguetung, setNeueVerguetung] = useState('');
    const [alteStundenlohn, setAlteStundenlohn] = useState('');
    const [neueStundenlohn, setNeueStundenlohn] = useState('');
    const [altGehaltMode, setAltGehaltMode] = useState<GehaltMode>('brutto');
    const [neuGehaltMode, setNeuGehaltMode] = useState<GehaltMode>('brutto');
    const wochenstundenAltManuellRef = useRef(false);
    const wochenstundenNeuManuellRef = useRef(false);
    const [bisherigeFiliale, setBisherigeFiliale] = useState('');
    const [neueFiliale, setNeueFiliale] = useState('');
    const [kuendigungsfristText, setKuendigungsfristText] = useState('');
    const [kuendigungsfristManuellBearbeitet, setKuendigungsfristManuellBearbeitet] = useState(false);
    const [betriebData, setBetriebData] = useState<BetriebFormData>({
        firmenname: '',
        strasse: '',
        plz: '',
        ort: '',
    });
    const [betriebLocks, setBetriebLocks] = useState<BetriebFieldLocks>({
        firmenname: false,
        strasse: false,
        plz: false,
        ort: false,
    });
    const [mitarbeiterData, setMitarbeiterData] = useState<MitarbeiterFormData>(INITIAL_MITARBEITER_DATA);
    const [selectedMitarbeiterId, setSelectedMitarbeiterId] = useState('');
    /** Nach Bestätigung: bisherige Vergütung/Stunden aus Stammdaten manuell überschreibbar */
    const [bisherigeStammdatenEditFreigegeben, setBisherigeStammdatenEditFreigegeben] = useState(false);
    const [invalidFields, setInvalidFields] = useState<Set<string>>(new Set());
    const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isPdfDownloading, setIsPdfDownloading] = useState(false);
    const [archiveFeedback, setArchiveFeedback] = useState<'idle' | 'success' | 'error' | 'draft'>('idle');
    const [archiveDocId, setArchiveDocId] = useState<string | null>(null);

    const printContainerRef = useRef<HTMLDivElement>(null);
    const clientData = useAppStore((state: any) => state.clientData);
    const settings = useAppStore((state: any) => state.settings);
    const { personalData, unproductivePersonalData, azubiAssistenzData } = usePersonalDataSelector();

    const stammdatenMitarbeiter = useMemo((): StammdatenMitarbeiterOption[] => {
        const mapGroup = (list: any[], group: string): StammdatenMitarbeiterOption[] =>
            (list || [])
                .filter((ma) => ma?.id && String(ma.name || '').trim())
                .map((ma) => ({
                    id: ma.id,
                    name: String(ma.name).trim(),
                    group,
                    wochenstunden: Number(ma.wochenstunden) || 0,
                    bruttolohnGesamt: Number(ma.bruttolohnGesamt ?? ma.grundgehalt) || 0,
                    stundenlohnGesamt: Number(ma.stundenlohnGesamt ?? ma.stundenlohnGrund) || 0,
                    adresse: String(ma.adresse || ma.info || '').trim(),
                }));

        return [
            ...mapGroup(personalData, 'Produktiv'),
            ...mapGroup(unproductivePersonalData, 'Unproduktiv'),
            ...mapGroup(azubiAssistenzData, 'Azubi / Assistenz'),
        ].sort((a, b) => a.name.localeCompare(b.name, 'de'));
    }, [personalData, unproductivePersonalData, azubiAssistenzData]);

    const wochensatz = useMemo(() => {
        const fromClient = Number(clientData?.wochensatz);
        if (Number.isFinite(fromClient) && fromClient > 0) return fromClient;
        const fromSettings = Number(settings?.global?.Wochensatz);
        if (Number.isFinite(fromSettings) && fromSettings > 0) return fromSettings;
        return 4.33;
    }, [clientData?.wochensatz, settings?.global?.Wochensatz]);

    const berechneteAltWochenstunden = useMemo(
        () => calculateWochenstundenFromPlan(alterArbeitsplan, wochensatz),
        [alterArbeitsplan, wochensatz]
    );

    const berechneteNeuWochenstunden = useMemo(
        () => calculateWochenstundenFromPlan(neuerArbeitsplan, wochensatz),
        [neuerArbeitsplan, wochensatz]
    );

    const altWochenstundenNum = parseNum(alteArbeitszeitStd);
    const neuWochenstundenNum = parseNum(neueArbeitszeitStd);

    const altEffektivBrutto = useMemo(() => {
        if (altGehaltMode === 'brutto') return parseNum(alteVerguetung);
        return calculateBruttoFromStundenlohn(parseNum(alteStundenlohn), altWochenstundenNum, wochensatz);
    }, [altGehaltMode, alteVerguetung, alteStundenlohn, altWochenstundenNum, wochensatz]);

    const neuEffektivBrutto = useMemo(() => {
        if (neuGehaltMode === 'brutto') return parseNum(neueVerguetung);
        return calculateBruttoFromStundenlohn(parseNum(neueStundenlohn), neuWochenstundenNum, wochensatz);
    }, [neuGehaltMode, neueVerguetung, neueStundenlohn, neuWochenstundenNum, wochensatz]);

    const altEffektivStundenlohn = useMemo(() => {
        if (altGehaltMode === 'stundenlohn') return parseNum(alteStundenlohn);
        return calculateStundenlohnFromBrutto(parseNum(alteVerguetung), altWochenstundenNum, wochensatz);
    }, [altGehaltMode, alteStundenlohn, alteVerguetung, altWochenstundenNum, wochensatz]);

    const neuEffektivStundenlohn = useMemo(() => {
        if (neuGehaltMode === 'stundenlohn') return parseNum(neueStundenlohn);
        return calculateStundenlohnFromBrutto(parseNum(neueVerguetung), neuWochenstundenNum, wochensatz);
    }, [neuGehaltMode, neueStundenlohn, neueVerguetung, neuWochenstundenNum, wochensatz]);

    const bgbFristBerechnung = useMemo(() => {
        const jahre = calculateBeschaeftigungsjahre(hauptvertragDatum);
        return calculateBgbKuendigungsfrist(jahre);
    }, [hauptvertragDatum]);

    useEffect(() => {
        if (vertragstyp !== 'aenderungskuendigung' || kuendigungsfristManuellBearbeitet) return;
        setKuendigungsfristText(bgbFristBerechnung.defaultKuendigungsfristText);
    }, [vertragstyp, bgbFristBerechnung, kuendigungsfristManuellBearbeitet]);

    // Neue Wochenstunden = immer aus Neuem Arbeitsplan (kein paralleles manuelles Soll-Feld)
    useEffect(() => {
        if (!aenderungsBereiche.arbeitszeit) return;
        if (berechneteNeuWochenstunden > 0) {
            wochenstundenNeuManuellRef.current = false;
            setNeueArbeitszeitStd(formatStdForNumberInput(berechneteNeuWochenstunden));
            clearFieldError('neue_arbeitszeit_std');
        }
    }, [aenderungsBereiche.arbeitszeit, berechneteNeuWochenstunden]);

    // Optionaler alter Plan kann bisherige Stunden nachziehen — Stammdaten bleiben manuell überschreibbar
    useEffect(() => {
        if (!aenderungsBereiche.arbeitszeit || wochenstundenAltManuellRef.current) return;
        if (berechneteAltWochenstunden > 0) {
            setAlteArbeitszeitStd(formatStdForNumberInput(berechneteAltWochenstunden));
        }
    }, [aenderungsBereiche.arbeitszeit, berechneteAltWochenstunden]);

    const aenderungsTexte = useMemo(
        () =>
            buildAenderungsTexte(
                vertragstyp,
                aenderungsBereiche,
                inkrafttretenDatum,
                alteArbeitszeitStd,
                neueArbeitszeitStd,
                neuerArbeitsplan,
                altEffektivBrutto,
                neuEffektivBrutto,
                altEffektivStundenlohn,
                neuEffektivStundenlohn,
                bisherigeFiliale,
                neueFiliale,
                sonstigesFreitext
            ),
        [
            vertragstyp,
            aenderungsBereiche,
            inkrafttretenDatum,
            alteArbeitszeitStd,
            neueArbeitszeitStd,
            neuerArbeitsplan,
            altEffektivBrutto,
            neuEffektivBrutto,
            altEffektivStundenlohn,
            neuEffektivStundenlohn,
            bisherigeFiliale,
            neueFiliale,
            sonstigesFreitext,
        ]
    );

    const resetForm = () => {
        setCurrentStep(1);
        setVertragstyp('aenderungsvertrag');
        setHauptvertragDatum('');
        setInkrafttretenDatum('');
        setAenderungsBereiche(INITIAL_AENDERUNGS_BEREICHE);
        setSonstigesFreitext('');
        setSondervereinbarungen('');
        setAlteArbeitszeitStd('');
        setNeueArbeitszeitStd('');
        setAlterArbeitsplan([]);
        setNeuerArbeitsplan([createArbeitszeitEntryFromPrevious()]);
        setAlteVerguetung('');
        setNeueVerguetung('');
        setAlteStundenlohn('');
        setNeueStundenlohn('');
        setAltGehaltMode('brutto');
        setNeuGehaltMode('brutto');
        wochenstundenAltManuellRef.current = false;
        wochenstundenNeuManuellRef.current = false;
        setBisherigeFiliale('');
        setNeueFiliale('');
        setKuendigungsfristText('');
        setKuendigungsfristManuellBearbeitet(false);
        setMitarbeiterData(INITIAL_MITARBEITER_DATA);
        setSelectedMitarbeiterId('');
        setBisherigeStammdatenEditFreigegeben(false);
        setInvalidFields(new Set());
        setDisclaimerAccepted(false);
        setIsSaving(false);
        setArchiveFeedback('idle');
        setArchiveDocId(null);
    };

    useEffect(() => {
        if (!isOpen) {
            resetForm();
            return;
        }

        const { betrieb, locks } = buildBetriebFromClient(clientData);
        resetForm();
        setBetriebData(betrieb);
        setBetriebLocks(locks);
        setArchiveDocId(initialData?.id || null);

        if (initialData) {
            if (initialData.vertragstyp === 'aenderungsvertrag' || initialData.vertragstyp === 'aenderungskuendigung') {
                setVertragstyp(initialData.vertragstyp);
            }
            if (typeof initialData.hauptvertragDatum === 'string') setHauptvertragDatum(initialData.hauptvertragDatum);
            if (typeof initialData.inkrafttretenDatum === 'string') setInkrafttretenDatum(initialData.inkrafttretenDatum);
            if (initialData.aenderungsBereiche && typeof initialData.aenderungsBereiche === 'object') {
                setAenderungsBereiche({ ...INITIAL_AENDERUNGS_BEREICHE, ...initialData.aenderungsBereiche });
            }
            if (typeof initialData.sonstigesFreitext === 'string') setSonstigesFreitext(initialData.sonstigesFreitext);
            if (typeof initialData.sondervereinbarungen === 'string') {
                setSondervereinbarungen(initialData.sondervereinbarungen);
            }
            if (typeof initialData.alteArbeitszeitStd === 'string') {
                setAlteArbeitszeitStd(initialData.alteArbeitszeitStd);
                wochenstundenAltManuellRef.current = Boolean(initialData.alteArbeitszeitStd);
            }
            if (typeof initialData.neueArbeitszeitStd === 'string') {
                setNeueArbeitszeitStd(initialData.neueArbeitszeitStd);
                wochenstundenNeuManuellRef.current = Boolean(initialData.neueArbeitszeitStd);
            }
            if (Array.isArray(initialData.alterArbeitsplan)) setAlterArbeitsplan(initialData.alterArbeitsplan);
            if (Array.isArray(initialData.neuerArbeitsplan) && initialData.neuerArbeitsplan.length > 0) {
                setNeuerArbeitsplan(initialData.neuerArbeitsplan);
            }
            if (typeof initialData.alteVerguetung === 'string') setAlteVerguetung(initialData.alteVerguetung);
            if (typeof initialData.neueVerguetung === 'string') setNeueVerguetung(initialData.neueVerguetung);
            if (typeof initialData.alteStundenlohn === 'string') setAlteStundenlohn(initialData.alteStundenlohn);
            if (typeof initialData.neueStundenlohn === 'string') setNeueStundenlohn(initialData.neueStundenlohn);
            if (initialData.altGehaltMode === 'brutto' || initialData.altGehaltMode === 'stundenlohn') {
                setAltGehaltMode(initialData.altGehaltMode);
            }
            if (initialData.neuGehaltMode === 'brutto' || initialData.neuGehaltMode === 'stundenlohn') {
                setNeuGehaltMode(initialData.neuGehaltMode);
            }
            if (typeof initialData.bisherigeFiliale === 'string') setBisherigeFiliale(initialData.bisherigeFiliale);
            if (typeof initialData.neueFiliale === 'string') setNeueFiliale(initialData.neueFiliale);
            if (typeof initialData.kuendigungsfristText === 'string') {
                setKuendigungsfristText(initialData.kuendigungsfristText);
                setKuendigungsfristManuellBearbeitet(Boolean(initialData.kuendigungsfristText));
            }
            if (initialData.betriebData && typeof initialData.betriebData === 'object') {
                setBetriebData({ ...betrieb, ...initialData.betriebData });
                setBetriebLocks({
                    firmenname: !isEmpty(String(initialData.betriebData.firmenname ?? betrieb.firmenname)),
                    strasse: !isEmpty(String(initialData.betriebData.strasse ?? betrieb.strasse)),
                    plz: !isEmpty(String(initialData.betriebData.plz ?? betrieb.plz)),
                    ort: !isEmpty(String(initialData.betriebData.ort ?? betrieb.ort)),
                });
            }
            if (initialData.mitarbeiterData && typeof initialData.mitarbeiterData === 'object') {
                setMitarbeiterData(normalizeMitarbeiterData(initialData.mitarbeiterData));
            }
            if (typeof initialData.selectedMitarbeiterId === 'string') {
                setSelectedMitarbeiterId(initialData.selectedMitarbeiterId);
            }
            const step = Number(initialData.currentStep);
            if (Number.isFinite(step) && step >= 1 && step <= 2) {
                setCurrentStep(step);
            }
            if (initialData.confirmedDisclaimer) {
                setDisclaimerAccepted(true);
            }
        }
    }, [isOpen, clientData, initialData]);

    const validateStep1 = (): string[] => {
        const errors: string[] = [];

        if (isEmpty(betriebData.firmenname)) errors.push('betrieb_firmenname');
        if (isEmpty(betriebData.strasse)) errors.push('betrieb_strasse');
        if (isEmpty(betriebData.plz)) errors.push('betrieb_plz');
        if (isEmpty(betriebData.ort)) errors.push('betrieb_ort');
        if (isEmpty(mitarbeiterData.name)) errors.push('mitarbeiter_name');
        if (isEmpty(mitarbeiterData.strasse)) errors.push('mitarbeiter_strasse');
        if (isEmpty(mitarbeiterData.plz)) errors.push('mitarbeiter_plz');
        if (isEmpty(mitarbeiterData.ort)) errors.push('mitarbeiter_ort');
        if (isEmpty(hauptvertragDatum)) errors.push('hauptvertrag_datum');
        if (isEmpty(inkrafttretenDatum)) errors.push('inkrafttreten_datum');
        if (vertragstyp === 'aenderungskuendigung' && isEmpty(kuendigungsfristText)) {
            errors.push('kuendigungsfrist_text');
        }
        if (!hasAnyAenderungsBereich(aenderungsBereiche)) errors.push('aenderungs_bereiche');
        if (aenderungsBereiche.arbeitszeit) {
            if (!isPositiveNum(alteArbeitszeitStd)) {
                errors.push('alte_arbeitszeit_std');
            }
            if (!(berechneteNeuWochenstunden > 0) && !isPositiveNum(neueArbeitszeitStd)) {
                errors.push('neue_arbeitszeit_std');
            }
            const gueltigeNeueEintraege = neuerArbeitsplan.filter(isArbeitszeitEntryComplete);
            if (gueltigeNeueEintraege.length === 0) {
                errors.push('neuer_arbeitsplan');
            }
            neuerArbeitsplan.forEach((entry, index) => {
                const hasPartialInput =
                    entry.von.trim() ||
                    entry.bis.trim() ||
                    entry.pauseMinuten.trim() ||
                    entry.tageProMonat.trim() ||
                    entry.rhythmus === 'monatlich';
                if (hasPartialInput && !isArbeitszeitEntryComplete(entry)) {
                    errors.push(`neuer_arbeitsplan_${index}`);
                }
            });
        }
        const brauchtWochenstunden =
            aenderungsBereiche.verguetung || aenderungsBereiche.arbeitszeit;
        if (brauchtWochenstunden) {
            if (!isPositiveNum(alteArbeitszeitStd)) errors.push('alte_arbeitszeit_std');
            if (!isPositiveNum(neueArbeitszeitStd)) errors.push('neue_arbeitszeit_std');
        }
        if (aenderungsBereiche.verguetung) {
            if (altGehaltMode === 'brutto') {
                if (!isPositiveNum(alteVerguetung)) errors.push('alte_verguetung');
            } else if (!isPositiveNum(alteStundenlohn)) {
                errors.push('alte_stundenlohn');
            }
            if (neuGehaltMode === 'brutto') {
                if (!isPositiveNum(neueVerguetung)) errors.push('neue_verguetung');
            } else if (!isPositiveNum(neueStundenlohn)) {
                errors.push('neue_stundenlohn');
            }
        }
        if (aenderungsBereiche.filiale) {
            if (isEmpty(bisherigeFiliale)) errors.push('bisherige_filiale');
            if (isEmpty(neueFiliale)) errors.push('neue_filiale');
        }
        if (aenderungsBereiche.sonstiges && isEmpty(sonstigesFreitext)) {
            errors.push('sonstiges_freitext');
        }
        if (hasAnyAenderungsBereich(aenderungsBereiche) && aenderungsTexte.length === 0) {
            errors.push('aenderungs_bereiche');
        }

        return errors;
    };

    const isFieldInvalid = (field: string) => invalidFields.has(field);

    const getInputClass = (baseClass: string, field: string) =>
        isFieldInvalid(field) ? `${baseClass} ${ERROR_INPUT_RING}` : baseClass;

    const clearFieldError = (field: string) => {
        setInvalidFields((prev) => {
            if (!prev.has(field)) return prev;
            const next = new Set(prev);
            next.delete(field);
            return next;
        });
    };

    const updateBetriebField = <K extends keyof BetriebFormData>(key: K, value: BetriebFormData[K]) => {
        setBetriebData((prev) => ({ ...prev, [key]: value }));
        clearFieldError(`betrieb_${key}`);
    };

    const updateMitarbeiterField = <K extends keyof MitarbeiterFormData>(key: K, value: MitarbeiterFormData[K]) => {
        setMitarbeiterData((prev) => ({ ...prev, [key]: value }));
        clearFieldError(`mitarbeiter_${key}`);
    };

    const getStammdatenBisherigeWerte = (ma: StammdatenMitarbeiterOption) => {
        const wochenstunden = ma.wochenstunden > 0 ? formatStdForNumberInput(ma.wochenstunden) : '';
        const brutto = ma.bruttolohnGesamt > 0 ? formatNumInputDe(ma.bruttolohnGesamt) : '';
        const stundenlohn = ma.stundenlohnGesamt > 0 ? formatNumInputDe(ma.stundenlohnGesamt) : '';
        const gehaltMode: GehaltMode =
            ma.bruttolohnGesamt > 0 ? 'brutto' : ma.stundenlohnGesamt > 0 ? 'stundenlohn' : 'brutto';
        return { wochenstunden, brutto, stundenlohn, gehaltMode };
    };

    const applyBisherigeWerteFromStammdaten = (ma: StammdatenMitarbeiterOption) => {
        const snap = getStammdatenBisherigeWerte(ma);

        if (snap.wochenstunden) {
            wochenstundenAltManuellRef.current = true; // Stammdaten-Wert als „bisher“ behalten
            setAlteArbeitszeitStd(snap.wochenstunden);
            clearFieldError('alte_arbeitszeit_std');
        }

        setAltGehaltMode(snap.gehaltMode);
        if (snap.brutto) {
            setAlteVerguetung(snap.brutto);
            clearFieldError('alte_verguetung');
        } else {
            setAlteVerguetung('');
        }
        if (snap.stundenlohn) {
            setAlteStundenlohn(snap.stundenlohn);
            clearFieldError('alte_stundenlohn');
        } else {
            setAlteStundenlohn('');
        }
    };

    const applyMitarbeiterFromStammdaten = (employeeId: string) => {
        const ma = stammdatenMitarbeiter.find((entry) => entry.id === employeeId);
        if (!ma) return;

        const parsedAdresse = parseLegacyMitarbeiterAdresse(ma.adresse);
        setMitarbeiterData({
            name: ma.name,
            strasse: parsedAdresse.strasse,
            plz: parsedAdresse.plz,
            ort: parsedAdresse.ort,
        });

        wochenstundenNeuManuellRef.current = false;
        applyBisherigeWerteFromStammdaten(ma);
        setBisherigeStammdatenEditFreigegeben(false);

        clearFieldError('mitarbeiter_name');
        clearFieldError('mitarbeiter_strasse');
        clearFieldError('mitarbeiter_plz');
        clearFieldError('mitarbeiter_ort');
    };

    const selectedStammdatenMitarbeiter = useMemo(
        () => stammdatenMitarbeiter.find((entry) => entry.id === selectedMitarbeiterId) || null,
        [stammdatenMitarbeiter, selectedMitarbeiterId]
    );

    const hatStammdatenVerguetung = Boolean(
        selectedStammdatenMitarbeiter &&
            (selectedStammdatenMitarbeiter.bruttolohnGesamt > 0 ||
                selectedStammdatenMitarbeiter.stundenlohnGesamt > 0)
    );
    const hatStammdatenWochenstunden = Boolean(
        selectedStammdatenMitarbeiter && selectedStammdatenMitarbeiter.wochenstunden > 0
    );
    const bisherigeStammdatenWerteGesperrt =
        Boolean(selectedStammdatenMitarbeiter) &&
        (hatStammdatenVerguetung || hatStammdatenWochenstunden) &&
        !bisherigeStammdatenEditFreigegeben;

    const bisherigeWerteWeichenVonStammdatenAb = useMemo(() => {
        if (!selectedStammdatenMitarbeiter) return false;
        const snap = getStammdatenBisherigeWerte(selectedStammdatenMitarbeiter);
        const numEq = (a: string, b: string) => parseNum(a) === parseNum(b);
        if (snap.wochenstunden && !numEq(alteArbeitszeitStd, snap.wochenstunden)) return true;
        if (altGehaltMode !== snap.gehaltMode) return true;
        if (snap.brutto && !numEq(alteVerguetung, snap.brutto)) return true;
        if (snap.stundenlohn && !numEq(alteStundenlohn, snap.stundenlohn)) return true;
        if (!snap.brutto && parseNum(alteVerguetung) > 0) return true;
        if (!snap.stundenlohn && parseNum(alteStundenlohn) > 0) return true;
        return false;
    }, [
        selectedStammdatenMitarbeiter,
        alteArbeitszeitStd,
        altGehaltMode,
        alteVerguetung,
        alteStundenlohn,
    ]);

    const resetBisherigeWerteAufStammdaten = () => {
        if (!selectedStammdatenMitarbeiter) return;
        applyBisherigeWerteFromStammdaten(selectedStammdatenMitarbeiter);
        setBisherigeStammdatenEditFreigegeben(false);
    };

    const requestBisherigeStammdatenEdit = () => {
        if (!selectedStammdatenMitarbeiter) return;
        const ok = window.confirm(
            'Stopp — das ist der Grundlohn / die bisherige Vergütung aus dem System (Personaldaten).\n\n' +
                'Dieser Wert kommt aus dem Datenblatt und sollte normalerweise nicht geändert werden. ' +
                'Wenn du ihn hier verstellst, kann das Dokument mit einem falschen Ausgangswert erstellt werden.\n\n' +
                'Wirklich manuell überschreiben?'
        );
        if (ok) setBisherigeStammdatenEditFreigegeben(true);
    };

    const handleStammdatenMitarbeiterChange = (employeeId: string) => {
        setSelectedMitarbeiterId(employeeId);
        if (employeeId) {
            applyMitarbeiterFromStammdaten(employeeId);
        } else {
            setBisherigeStammdatenEditFreigegeben(false);
        }
    };

    const renderStammdatenResetButton = (label = 'Stammdaten') => {
        if (!selectedStammdatenMitarbeiter || bisherigeStammdatenWerteGesperrt) return null;
        return (
            <button
                type="button"
                onClick={resetBisherigeWerteAufStammdaten}
                title="Bisherige Werte aus dem Datenblatt / Personaldaten wiederherstellen"
                className={`inline-flex items-center gap-1.5 shrink-0 rounded-lg border px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-wider transition-colors ${
                    bisherigeWerteWeichenVonStammdatenAb
                        ? 'border-[#E6C299]/50 bg-[#E6C299]/15 text-pe-accent-text dark:text-[#E6C299] hover:bg-[#E6C299]/25'
                        : 'border-pe-border dark:border-slate-700 bg-pe-elevated dark:bg-[#0a0a0a] text-pe-muted dark:text-slate-400 hover:text-[#E6C299]'
                }`}
            >
                <RotateCcw className="h-3.5 w-3.5" />
                {label}
            </button>
        );
    };

    const renderBisherigeStammdatenUnlockButton = () => {
        if (!bisherigeStammdatenWerteGesperrt) return null;
        return (
            <button
                type="button"
                onClick={requestBisherigeStammdatenEdit}
                className="inline-flex items-center gap-1.5 shrink-0 rounded-lg border border-amber-500/40 bg-amber-500/10 px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-wider text-amber-700 dark:text-amber-300 hover:bg-amber-500/20 transition-colors"
            >
                <Lock className="h-3.5 w-3.5" />
                Ändern…
            </button>
        );
    };

    const resetAenderungsEingabenForBereich = (key: AenderungsBereichKey) => {
        if (key === 'arbeitszeit') {
            setAlteArbeitszeitStd('');
            setNeueArbeitszeitStd('');
            setAlterArbeitsplan([]);
            setNeuerArbeitsplan([createArbeitszeitEntryFromPrevious()]);
            wochenstundenAltManuellRef.current = false;
            wochenstundenNeuManuellRef.current = false;
            clearFieldError('alte_arbeitszeit_std');
            clearFieldError('neue_arbeitszeit_std');
            clearFieldError('neuer_arbeitsplan');
        }
        if (key === 'verguetung') {
            setAlteVerguetung('');
            setNeueVerguetung('');
            setAlteStundenlohn('');
            setNeueStundenlohn('');
            setAltGehaltMode('brutto');
            setNeuGehaltMode('brutto');
            clearFieldError('alte_verguetung');
            clearFieldError('neue_verguetung');
            clearFieldError('alte_stundenlohn');
            clearFieldError('neue_stundenlohn');
        }
        if (key === 'filiale') {
            setBisherigeFiliale('');
            setNeueFiliale('');
            clearFieldError('bisherige_filiale');
            clearFieldError('neue_filiale');
        }
        if (key === 'sonstiges') {
            setSonstigesFreitext('');
            clearFieldError('sonstiges_freitext');
        }
    };

    const handleAenderungsBereichToggle = (key: AenderungsBereichKey, checked: boolean) => {
        setAenderungsBereiche((prev) => ({ ...prev, [key]: checked }));
        if (!checked) resetAenderungsEingabenForBereich(key);
        clearFieldError('aenderungs_bereiche');
    };

    const renderGehaltModeToggle = (
        mode: GehaltMode,
        onChange: (mode: GehaltMode) => void,
        label: string,
        disabled = false
    ) => (
        <div>
            <label className={LABEL_CLASS}>{label}</label>
            <div
                className={`flex rounded-lg border border-pe-border dark:border-slate-700 bg-pe-input dark:bg-black p-1 gap-1 mt-1 ${
                    disabled ? 'opacity-70 pointer-events-none' : ''
                }`}
            >
                {([
                    { key: 'brutto' as GehaltMode, label: 'Bruttolohn' },
                    { key: 'stundenlohn' as GehaltMode, label: 'Stundenlohn' },
                ]).map((opt) => (
                    <button
                        key={opt.key}
                        type="button"
                        disabled={disabled}
                        onClick={() => onChange(opt.key)}
                        className={`flex-1 px-2 py-2 rounded-md text-[10px] font-bold uppercase transition-colors ${
                            mode === opt.key
                                ? 'bg-[#E6C299]/20 text-pe-accent-text dark:text-[#E6C299] border border-[#E6C299]/40'
                                : 'text-pe-muted dark:text-slate-500 border border-transparent'
                        }`}
                    >
                        {opt.label}
                    </button>
                ))}
            </div>
        </div>
    );

    const renderFieldError = (field: string) =>
        isFieldInvalid(field) ? (
            <p className="text-[10px] text-red-400 mt-1">Dieses Feld ist erforderlich</p>
        ) : null;

    const renderBetriebField = (field: keyof BetriebFormData, label: string) => {
        const lockKey = field as keyof BetriebFieldLocks;
        const fieldId = `betrieb_${field}`;
        const isLocked = betriebLocks[lockKey];

        return (
            <div>
                <label className={LABEL_CLASS}>{label}</label>
                <input
                    type="text"
                    value={betriebData[field]}
                    readOnly={isLocked}
                    onChange={(e) => !isLocked && updateBetriebField(field, e.target.value)}
                    className={getInputClass(isLocked ? READONLY_INPUT_CLASS : INPUT_CLASS, fieldId)}
                />
                {renderFieldError(fieldId)}
            </div>
        );
    };

    const renderDateField = (label: string, value: string, onChange: (v: string) => void, fieldId: string) => (
        <div>
            <label className={LABEL_CLASS}>{label}</label>
            <input
                type="date"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className={getInputClass(DATE_INPUT_CLASS, fieldId)}
            />
            {renderFieldError(fieldId)}
        </div>
    );

    const renderSegmentedControl = () => (
        <div className="flex flex-col sm:flex-row rounded-lg border border-pe-border dark:border-slate-700 bg-pe-input dark:bg-black p-1 gap-1">
            {VERTRAGSTYP_OPTIONS.map((opt) => (
                <button
                    key={opt.key}
                    type="button"
                    onClick={() => {
                        setVertragstyp(opt.key);
                        setInvalidFields(new Set());
                        if (opt.key === 'aenderungskuendigung') {
                            setKuendigungsfristManuellBearbeitet(false);
                        }
                    }}
                    className={`flex-1 min-w-0 px-1.5 py-2 rounded-md text-[7px] sm:text-[8px] md:text-[9px] font-bold uppercase tracking-normal leading-tight text-center whitespace-normal break-words transition-colors ${
                        vertragstyp === opt.key
                            ? 'bg-[#E6C299]/20 text-pe-accent-text dark:text-[#E6C299] border border-[#E6C299]/40'
                            : 'text-stone-500 dark:text-slate-500 hover:text-stone-700 dark:hover:text-slate-300 border border-transparent'
                    }`}
                    title={opt.label}
                >
                    {opt.label}
                </button>
            ))}
        </div>
    );

    const renderAenderungsTexteBlock = () => (
        <div className="av-doc-section">
            <h3>Neuregelungen</h3>
            {aenderungsBereiche.arbeitszeit && (altWochenstundenNum > 0 || neuWochenstundenNum > 0) ? (
                <div className="av-summary-box">
                    <strong>Wochenstunden:</strong>{' '}
                    bisher{' '}
                    <strong>
                        {altWochenstundenNum > 0
                            ? `${formatStdForDisplay(altWochenstundenNum)} Std./Woche`
                            : '____________'}
                    </strong>
                    {' → '}
                    neu{' '}
                    <strong>
                        {neuWochenstundenNum > 0
                            ? `${formatStdForDisplay(neuWochenstundenNum)} Std./Woche`
                            : '____________'}
                    </strong>
                </div>
            ) : null}
            {aenderungsTexte.map((text) => (
                <p key={text} className={DOC_P_CLASS}>
                    {text}
                </p>
            ))}
            {aenderungsBereiche.arbeitszeit ? (
                <ArbeitszeitPlanDocumentTable
                    plan={neuerArbeitsplan}
                    title="Neuer wöchentlicher Arbeitsplan"
                    wochensatz={wochensatz}
                    wochenstundenOverride={neuWochenstundenNum > 0 ? neuWochenstundenNum : null}
                />
            ) : null}
        </div>
    );

    const renderAenderungsvertragDocument = () => (
        <>
            <div className="av-doc-section">
                <h3>Einvernehmliche Vertragsanpassung</h3>
                <p className={DOC_P_CLASS}>
                    Guten Tag <strong>{mitarbeiterData.name || '____________'}</strong>,
                </p>
                <p className={DOC_P_CLASS}>
                    die Parteien vereinbaren einvernehmlich die Anpassung des zwischen ihnen bestehenden
                    Arbeitsverhältnisses auf Grundlage des Hauptarbeitsvertrages vom{' '}
                    <strong>{formatDisplayDate(hauptvertragDatum)}</strong>.
                </p>
                <p className={DOC_P_CLASS}>
                    Ab dem <strong>{formatDisplayDate(inkrafttretenDatum)}</strong> gelten folgende Neuregelungen:
                </p>
            </div>
            {renderAenderungsTexteBlock()}
            <div className="av-doc-section">
                <h3>Schlussbestimmungen</h3>
                <p className={DOC_P_CLASS}>
                    Die Parteien erklären ausdrücklich, dass die Änderung einvernehmlich und freiwillig erfolgt.
                </p>
                {renderSondervereinbarungenInDocument(sondervereinbarungen, 'brief')}
                <p className={DOC_P_CLASS}>
                    {BESTANDSSCHUTZ_KLAUSEL.replace(
                        'des Hauptarbeitsvertrages',
                        `des Hauptarbeitsvertrages vom ${formatDisplayDate(hauptvertragDatum)}`
                    )}
                </p>
            </div>
        </>
    );

    const renderAenderungskuendigungDocument = () => (
        <>
            <div className="av-doc-section">
                <h3>Änderungskündigung</h3>
                <p className={DOC_P_CLASS}>
                    Guten Tag <strong>{mitarbeiterData.name || '____________'}</strong>,
                </p>
                <p className={DOC_P_CLASS}>
                    wir kündigen hiermit das auf dem Hauptarbeitsvertrag vom{' '}
                    <strong>{formatDisplayDate(hauptvertragDatum)}</strong> beruhende Arbeitsverhältnis ordentlich und
                    fristgerecht unter Einhaltung der Kündigungsfrist von{' '}
                    <strong>{kuendigungsfristText.trim() || '____________'}</strong> zum{' '}
                    <strong>{formatDisplayDate(inkrafttretenDatum)}</strong>.
                </p>
                <p className={DOC_P_CLASS}>
                    Gleichzeitig bieten wir Ihnen an, das Arbeitsverhältnis ab dem{' '}
                    <strong>{formatDisplayDate(inkrafttretenDatum)}</strong> unter folgenden geänderten Bedingungen
                    fortzusetzen:
                </p>
            </div>
            {renderAenderungsTexteBlock()}
            <div className="av-doc-section">
                <h3>Hinweise &amp; Schlussbestimmungen</h3>
                <p className={DOC_P_CLASS}>{KSchG_HINWEIS}</p>
                {renderSondervereinbarungenInDocument(sondervereinbarungen, 'brief')}
                <p className={DOC_P_CLASS}>
                    {BESTANDSSCHUTZ_KLAUSEL.replace(
                        'des Hauptarbeitsvertrages',
                        `des Hauptarbeitsvertrages vom ${formatDisplayDate(hauptvertragDatum)}`
                    )}
                </p>
            </div>
        </>
    );

    const getDocumentTitle = (): string =>
        vertragstyp === 'aenderungsvertrag' ? 'Änderungsvertrag' : 'Änderungskündigung';

    const renderContractDocument = () => {
        const ortDatum = `${betriebData.ort || '____________'}, ${formatTodayGerman()}`;

        return (
        <div ref={printContainerRef} id="pepapers-vertragsanpassung-print" className="w-full h-auto overflow-visible">
            <style>{VA_CONTRACT_PDF_CSS}</style>
            <div className={`${A4_PREVIEW_PAGE_CLASS} min-h-[297mm]`}>
                <div className="pdf-letterhead text-center">
                    <h1>{getDocumentTitle()}</h1>
                    <h2>PreisExpert · PEPapers</h2>
                </div>

                <div className="av-meta-grid">
                    <div className="av-meta-col">
                        <p className="av-meta-label">Arbeitgeber</p>
                        <p>
                            <strong>{betriebData.firmenname || '____________'}</strong>
                            {betriebData.strasse ? (
                                <>
                                    <br />
                                    {betriebData.strasse}
                                </>
                            ) : null}
                            {betriebData.plz || betriebData.ort ? (
                                <>
                                    <br />
                                    {[betriebData.plz, betriebData.ort].filter(Boolean).join(' ')}
                                </>
                            ) : null}
                            <span className="av-meta-date">Datum: {formatTodayGerman()}</span>
                        </p>
                    </div>
                    <div className="av-meta-col">
                        <p className="av-meta-label">Arbeitnehmer/in</p>
                        <p>
                            <strong>{mitarbeiterData.name || '____________'}</strong>
                            {mitarbeiterData.strasse ? (
                                <>
                                    <br />
                                    {mitarbeiterData.strasse}
                                </>
                            ) : null}
                            {mitarbeiterData.plz || mitarbeiterData.ort ? (
                                <>
                                    <br />
                                    {[mitarbeiterData.plz, mitarbeiterData.ort].filter(Boolean).join(' ')}
                                </>
                            ) : !mitarbeiterData.strasse ? (
                                <>
                                    <br />
                                    ____________
                                </>
                            ) : null}
                        </p>
                    </div>
                </div>

                {vertragstyp === 'aenderungsvertrag' && renderAenderungsvertragDocument()}
                {vertragstyp === 'aenderungskuendigung' && renderAenderungskuendigungDocument()}

                <div className="av-doc-section">
                    <p className={DOC_P_CLASS}>
                        Sollte eine Bestimmung dieser Vereinbarung unwirksam sein, wird die Wirksamkeit der übrigen
                        Bestimmungen hiervon nicht berührt. Die Parteien verpflichten sich, die unwirksame Bestimmung
                        durch eine Vereinbarung zu ersetzen, die der unwirksamen Bestimmung in Interessenlage und
                        Bedeutung möglichst nahe kommt.
                    </p>
                </div>

                <table
                    className="va-sig-table"
                    style={{
                        width: '100%',
                        marginTop: '32px',
                        borderCollapse: 'collapse',
                        tableLayout: 'fixed',
                    }}
                >
                    <tbody>
                        <tr>
                            <td style={{ width: '50%', paddingRight: '24px', verticalAlign: 'top' }}>
                                <p style={{ margin: 0, fontSize: '11px', color: '#475569' }}>
                                    Ort, Datum: {ortDatum}
                                </p>
                                <div
                                    className="va-sig-line-soft"
                                    style={{
                                        width: '200px',
                                        borderBottom: '2px solid #1e293b',
                                        marginTop: '28px',
                                        marginBottom: '6px',
                                    }}
                                    aria-hidden="true"
                                />
                                <p style={{ margin: 0, fontSize: '11px', fontWeight: 600, color: '#0f172a' }}>
                                    Unterschrift Arbeitgeber/in
                                </p>
                                <p style={{ margin: 0, fontSize: '11px', color: '#64748b' }}>
                                    {betriebData.firmenname || 'Arbeitgeber'}
                                </p>
                            </td>
                            <td style={{ width: '50%', paddingLeft: '24px', verticalAlign: 'top' }}>
                                <div style={{ minHeight: '16px' }} aria-hidden="true" />
                                <div
                                    className="va-sig-line-soft"
                                    style={{
                                        width: '200px',
                                        borderBottom: '2px solid #1e293b',
                                        marginTop: '28px',
                                        marginBottom: '6px',
                                    }}
                                    aria-hidden="true"
                                />
                                <p style={{ margin: 0, fontSize: '11px', fontWeight: 600, color: '#0f172a' }}>
                                    Unterschrift Arbeitnehmer/in
                                </p>
                                <p style={{ margin: 0, fontSize: '11px', color: '#64748b' }}>
                                    {mitarbeiterData.name || 'Arbeitnehmer/in'}
                                </p>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
        );
    };

    const handleWeiter = () => {
        const errors = validateStep1();
        if (errors.length > 0) {
            setInvalidFields(new Set(errors));
            return;
        }
        setInvalidFields(new Set());
        setCurrentStep(2);
    };

    const handleZurueck = () => {
        setInvalidFields(new Set());
        setCurrentStep(1);
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    const isActionLocked = !disclaimerAccepted;

    const getActionButtonClass = (baseClass: string) =>
        `${baseClass} ${isActionLocked ? 'opacity-50 cursor-not-allowed' : ''}`;

    const getPrintHtml = () =>
        printContainerRef.current?.innerHTML ||
        document.getElementById('pepapers-vertragsanpassung-print')?.innerHTML ||
        '';

    const handleDownloadPdf = async () => {
        if (isActionLocked || isPdfDownloading) return;
        const html = getPrintHtml();
        if (!html.trim()) {
            window.alert('Dokumentinhalt fehlt — bitte Vorschau prüfen.');
            return;
        }
        setIsPdfDownloading(true);
        try {
            const dateIso = new Date().toISOString().slice(0, 10);
            await downloadHtmlAsPdf({
                html,
                filename: `${getDocumentTitle()}_${dateIso}.pdf`,
                scrubMode: 'minimal',
                splitPrintBreaks: true,
                capturePadPx: 0,
                letterheadOnBreaks: false,
                extraCss: VA_CONTRACT_PDF_CSS,
            });
        } catch (err: unknown) {
            console.error('PEPapers Vertragsanpassung PDF-Download fehlgeschlagen:', err);
            const message = err instanceof Error ? err.message : 'Unbekannter Fehler';
            window.alert('PDF konnte nicht heruntergeladen werden: ' + message);
        } finally {
            setIsPdfDownloading(false);
        }
    };

    const handlePrintIsolated = () => {
        if (isActionLocked || isPdfDownloading) return;

        const iframe = document.createElement('iframe');
        iframe.style.position = 'absolute';
        iframe.style.width = '0px';
        iframe.style.height = '0px';
        iframe.style.border = 'none';
        document.body.appendChild(iframe);

        const printWindow = iframe.contentWindow;
        if (!printWindow) {
            document.body.removeChild(iframe);
            return;
        }

        const printContent = getPrintHtml();

        const doc = printWindow.document;
        doc.open();
        doc.write(`
    <!DOCTYPE html>
    <html>
    <head>
      <title>${getDocumentTitle()}</title>
      <style>${VA_PRINT_CSS}</style>
    </head>
    <body>${printContent}</body>
    </html>
  `);
        doc.close();

        setTimeout(() => {
            printWindow.focus();
            printWindow.print();
            setTimeout(() => {
                if (iframe.parentNode) document.body.removeChild(iframe);
            }, 1000);
        }, 500);
    };

    const handleArchive = async (asDraft = false) => {
        if (isSaving) return;
        if (!asDraft && isActionLocked) return;

        setIsSaving(true);
        setArchiveFeedback('idle');

        const htmlContent =
            printContainerRef.current?.innerHTML ||
            document.getElementById('pepapers-vertragsanpassung-print')?.innerHTML ||
            '';

        try {
            const id = await savePePapersArchiveDoc({
                id: archiveDocId,
                documentType: 'vertragsanpassung-kombi',
                status: asDraft ? 'entwurf' : 'final',
                salonName: betriebData.firmenname || 'Unbekannter Salon',
                mitarbeiterName: mitarbeiterData.name.trim(),
                confirmedDisclaimer: asDraft ? disclaimerAccepted : true,
                currentStep,
                payload: {
                    vertragstyp,
                    hauptvertragDatum,
                    inkrafttretenDatum,
                    aenderungsBereiche,
                    sonstigesFreitext,
                    sondervereinbarungen: sondervereinbarungen.trim() || null,
                    alteArbeitszeitStd,
                    neueArbeitszeitStd,
                    alterArbeitsplan,
                    neuerArbeitsplan,
                    alteVerguetung,
                    neueVerguetung,
                    alteStundenlohn,
                    neueStundenlohn,
                    altGehaltMode,
                    neuGehaltMode,
                    altEffektivBrutto,
                    neuEffektivBrutto,
                    altEffektivStundenlohn,
                    neuEffektivStundenlohn,
                    wochensatz,
                    bisherigeFiliale,
                    neueFiliale,
                    kuendigungsfristText,
                    betriebData,
                    mitarbeiterData: {
                        ...mitarbeiterData,
                        adresse: formatMitarbeiterAdresseInline(mitarbeiterData),
                    },
                    selectedMitarbeiterId,
                    generatedHtml: htmlContent,
                },
            });
            setArchiveDocId(id);
            setArchiveFeedback(asDraft ? 'draft' : 'success');
            onArchived?.();
            if (asDraft) {
                setTimeout(() => {
                    onClose();
                }, 600);
            } else {
                setTimeout(() => setArchiveFeedback('idle'), 3000);
            }
        } catch (error) {
            console.error('PEPapers Vertragsanpassung Archivierung fehlgeschlagen:', error);
            setArchiveFeedback('error');
        } finally {
            setIsSaving(false);
        }
    };

    if (!isOpen) return null;

    const modalWidthClass = currentStep === 2 ? 'max-w-4xl' : 'max-w-5xl';

    return createPortal(
        <>
            <style>{`
                input.kombi-date-input[type='date']::-webkit-calendar-picker-indicator {
                    filter: invert(1);
                    cursor: pointer;
                    opacity: 1;
                }
            `}</style>
            <div
                className="fixed inset-0 bg-stone-900/70 dark:bg-black/90 z-[100000] backdrop-blur-md print:hidden"
                onClick={handleClose}
                aria-hidden="true"
            />
            <div className="fixed inset-x-0 top-[8.5rem] md:top-36 bottom-0 z-[100001] flex items-start justify-center overflow-hidden p-4 pb-8 font-sans print:inset-0 print:top-0 print:overflow-visible print:p-0">
                <div
                    className={`bg-pe-card dark:bg-[#111] rounded-2xl shadow-[0_0_40px_rgba(230,194,153,0.1)] border border-[#E6C299]/30 w-full ${modalWidthClass} max-h-[calc(100vh-9.5rem)] md:max-h-[calc(100vh-10rem)] flex min-h-0 flex-col justify-between overflow-hidden relative transition-all duration-200 my-2 print:shadow-none print:border-0 print:max-w-none print:max-h-none print:rounded-none print:my-0 print:bg-white`}
                    onClick={(e) => e.stopPropagation()}
                >
                    <button
                        type="button"
                        onClick={handleClose}
                        className="absolute top-4 right-4 text-pe-muted dark:text-slate-400 hover:text-pe-text dark:hover:text-white bg-pe-elevated dark:bg-[#1a1a1a] hover:bg-pe-hover dark:hover:bg-[#222] rounded-lg p-2 transition-colors border border-pe-border-strong dark:border-[#333] z-[60] print:hidden"
                        aria-label="Schließen"
                    >
                        <X className="w-4 h-4" />
                    </button>

                    <div className="bg-pe-elevated dark:bg-[#111622] z-50 shrink-0 print:hidden border-b border-pe-border dark:border-[#222]">
                        <div className="px-6 pt-10 pb-4">
                            <h3 className="text-lg font-bold text-pe-accent-text dark:text-[#E6C299] text-center uppercase tracking-widest pr-8">
                                Änderungsvertrag / Änderungskündigung
                            </h3>
                            <p className="text-center text-xs text-pe-muted dark:text-slate-400 uppercase tracking-widest mt-2">
                                Schritt {currentStep} von 2: {STEP_LABELS[currentStep]}
                            </p>
                        </div>
                    </div>

                    <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 custom-scrollbar print:p-0 print:overflow-visible">
                        {currentStep === 1 && (
                            <div className="space-y-6">
                                <div>
                                    <p className="text-[10px] font-bold uppercase tracking-widest text-pe-accent-text dark:text-[#E6C299]/80 mb-3 border-b border-pe-border dark:border-[#222] pb-2">
                                        Dokumenten-Typ
                                    </p>
                                    {renderSegmentedControl()}
                                </div>

                                <div>
                                    <p className="text-[10px] font-bold uppercase tracking-widest text-pe-accent-text dark:text-[#E6C299]/80 mb-3 border-b border-pe-border dark:border-[#222] pb-2">
                                        Betriebsdaten
                                    </p>
                                    <div className="space-y-4">
                                        {renderBetriebField('firmenname', 'Firma / Salonname')}
                                        {renderBetriebField('strasse', 'Straße & Hausnummer')}
                                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                            {renderBetriebField('plz', 'PLZ')}
                                            <div className="sm:col-span-2">{renderBetriebField('ort', 'Ort')}</div>
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <p className="text-[10px] font-bold uppercase tracking-widest text-pe-accent-text dark:text-[#E6C299]/80 mb-3 border-b border-pe-border dark:border-[#222] pb-2">
                                        Mitarbeiter-Daten
                                    </p>
                                    <div className="space-y-4">
                                        <div>
                                            <label className={LABEL_CLASS}>Mitarbeiter aus Personaldaten</label>
                                            <select
                                                value={selectedMitarbeiterId}
                                                onChange={(e) => handleStammdatenMitarbeiterChange(e.target.value)}
                                                className={INPUT_CLASS}
                                            >
                                                <option value="">— Manuell eingeben —</option>
                                                {stammdatenMitarbeiter.map((ma) => (
                                                    <option key={ma.id} value={ma.id}>
                                                        {ma.name}
                                                        {ma.wochenstunden > 0
                                                            ? ` (${formatStdForDisplay(ma.wochenstunden)} Std./Wo.)`
                                                            : ''}
                                                        {' · '}
                                                        {ma.group}
                                                    </option>
                                                ))}
                                            </select>
                                            {stammdatenMitarbeiter.length === 0 && (
                                                <p className="text-[10px] text-pe-muted dark:text-slate-500 mt-1">
                                                    Keine Personaldaten hinterlegt – bitte Name und Werte manuell eintragen.
                                                </p>
                                            )}
                                            {selectedMitarbeiterId && (
                                                <p className="text-[10px] text-pe-accent-text dark:text-[#E6C299]/80 mt-1">
                                                    Stammdaten übernommen. Bisherige Vergütung/Wochenstunden sind geschützt — Änderung nur nach Bestätigung.
                                                </p>
                                            )}
                                        </div>
                                        <div>
                                            <label className={LABEL_CLASS}>Name des Mitarbeiters</label>
                                            <input
                                                type="text"
                                                value={mitarbeiterData.name}
                                                onChange={(e) => updateMitarbeiterField('name', e.target.value)}
                                                className={getInputClass(INPUT_CLASS, 'mitarbeiter_name')}
                                            />
                                            {renderFieldError('mitarbeiter_name')}
                                        </div>
                                        <div>
                                            <label className={LABEL_CLASS}>Straße &amp; Hausnummer</label>
                                            <input
                                                type="text"
                                                value={mitarbeiterData.strasse}
                                                onChange={(e) => updateMitarbeiterField('strasse', e.target.value)}
                                                placeholder="z. B. Musterstraße 12"
                                                className={getInputClass(INPUT_CLASS, 'mitarbeiter_strasse')}
                                            />
                                            {renderFieldError('mitarbeiter_strasse')}
                                        </div>
                                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                            <div>
                                                <label className={LABEL_CLASS}>PLZ</label>
                                                <input
                                                    type="text"
                                                    inputMode="numeric"
                                                    value={mitarbeiterData.plz}
                                                    onChange={(e) => updateMitarbeiterField('plz', e.target.value)}
                                                    placeholder="12345"
                                                    className={getInputClass(INPUT_CLASS, 'mitarbeiter_plz')}
                                                />
                                                {renderFieldError('mitarbeiter_plz')}
                                            </div>
                                            <div className="sm:col-span-2">
                                                <label className={LABEL_CLASS}>Ort</label>
                                                <input
                                                    type="text"
                                                    value={mitarbeiterData.ort}
                                                    onChange={(e) => updateMitarbeiterField('ort', e.target.value)}
                                                    placeholder="Musterstadt"
                                                    className={getInputClass(INPUT_CLASS, 'mitarbeiter_ort')}
                                                />
                                                {renderFieldError('mitarbeiter_ort')}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <p className="text-[10px] font-bold uppercase tracking-widest text-pe-accent-text dark:text-[#E6C299]/80 mb-3 border-b border-pe-border dark:border-[#222] pb-2">
                                        Vertragsbezug
                                    </p>
                                    <div className="space-y-4">
                                        {renderDateField(
                                            'Datum des ursprünglichen Hauptarbeitsvertrags',
                                            hauptvertragDatum,
                                            (v) => {
                                                setHauptvertragDatum(v);
                                                clearFieldError('hauptvertrag_datum');
                                            },
                                            'hauptvertrag_datum'
                                        )}

                                        {renderDateField(
                                            'Inkrafttreten der Änderung / Kündigungstermin',
                                            inkrafttretenDatum,
                                            (v) => {
                                                setInkrafttretenDatum(v);
                                                clearFieldError('inkrafttreten_datum');
                                            },
                                            'inkrafttreten_datum'
                                        )}
                                        {vertragstyp === 'aenderungskuendigung' && (
                                            <>
                                                <p className="text-sm text-pe-muted dark:text-slate-300 bg-pe-card dark:bg-[#15171e] border border-[#E6C299]/20 rounded-lg px-4 py-3 leading-relaxed">
                                                    {buildBgbFristInfoText(bgbFristBerechnung)}
                                                </p>
                                                <div>
                                                    <label className={LABEL_CLASS}>
                                                        Kündigungsfrist für Textausgabe (editierbar)
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={kuendigungsfristText}
                                                        onChange={(e) => {
                                                            setKuendigungsfristText(e.target.value);
                                                            setKuendigungsfristManuellBearbeitet(true);
                                                            clearFieldError('kuendigungsfrist_text');
                                                        }}
                                                        placeholder={bgbFristBerechnung.defaultKuendigungsfristText}
                                                        className={getInputClass(INPUT_CLASS, 'kuendigungsfrist_text')}
                                                    />
                                                    {renderFieldError('kuendigungsfrist_text')}
                                                </div>
                                            </>
                                        )}
                                        <div>
                                            <label className={LABEL_CLASS}>Zu ändernde Bereiche (Mehrfachauswahl möglich)</label>
                                            <div className="space-y-2 mt-2">
                                                {AENDERUNGS_BEREICH_OPTIONS.map((opt) => {
                                                    const selected = aenderungsBereiche[opt.key];
                                                    return (
                                                        <button
                                                            key={opt.key}
                                                            type="button"
                                                            onClick={() => handleAenderungsBereichToggle(opt.key, !selected)}
                                                            className={`w-full flex items-center gap-3 p-3 rounded-lg border text-left transition-all ${
                                                                selected
                                                                    ? 'border-[#E6C299] bg-[#E6C299]/15 shadow-[0_0_0_1px_rgba(230,194,153,0.35)]'
                                                                    : 'border-pe-border dark:border-slate-700 bg-pe-card dark:bg-[#15171e] hover:border-[#E6C299]/30'
                                                            }`}
                                                        >
                                                            <span
                                                                className={`w-5 h-5 shrink-0 rounded border flex items-center justify-center text-xs font-black ${
                                                                    selected
                                                                        ? 'bg-[#E6C299] border-[#E6C299] text-black'
                                                                        : 'border-pe-border-strong dark:border-slate-600 bg-pe-input dark:bg-black text-transparent'
                                                                }`}
                                                            >
                                                                ✓
                                                            </span>
                                                            <span className={`text-sm font-medium ${selected ? 'text-pe-accent-text dark:text-[#E6C299]' : 'text-pe-muted dark:text-slate-300'}`}>
                                                                {opt.label}
                                                            </span>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                            {renderFieldError('aenderungs_bereiche')}
                                        </div>
                                        {(aenderungsBereiche.arbeitszeit || aenderungsBereiche.verguetung) && (
                                            <div className="space-y-3 p-4 rounded-lg border border-[#E6C299]/25 bg-pe-card dark:bg-[#15171e]">
                                                <div className="flex items-start justify-between gap-3">
                                                    <p className="text-sm font-semibold text-pe-accent-text dark:text-[#E6C299]">
                                                        Wochenstunden
                                                    </p>
                                                    {!aenderungsBereiche.verguetung ? (
                                                        <>
                                                            {renderBisherigeStammdatenUnlockButton()}
                                                            {renderStammdatenResetButton()}
                                                        </>
                                                    ) : null}
                                                </div>
                                                <p className="text-xs text-pe-muted dark:text-slate-400 leading-relaxed">
                                                    Für Vergütung und Dokument. Wochensatz-Faktor:{' '}
                                                    <strong>{wochensatz.toFixed(2).replace('.', ',')}</strong>.
                                                    {aenderungsBereiche.arbeitszeit
                                                        ? ' Die neuen Wochenstunden kommen automatisch aus dem neuen Tagesplan unten — nicht doppelt eintragen.'
                                                        : null}
                                                </p>
                                                {bisherigeStammdatenWerteGesperrt &&
                                                    hatStammdatenWochenstunden &&
                                                    !aenderungsBereiche.verguetung && (
                                                        <p className="text-[11px] leading-relaxed rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-amber-800 dark:text-amber-200">
                                                            Bisherige Wochenstunden kommen aus dem System und sind gesperrt.
                                                            Zum Überschreiben zuerst „Ändern…“ bestätigen.
                                                        </p>
                                                    )}
                                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                    <div>
                                                        <label className={LABEL_CLASS}>Bisherige Wochenstunden</label>
                                                        <input
                                                            type="number"
                                                            min={0.1}
                                                            step={0.5}
                                                            value={alteArbeitszeitStd}
                                                            readOnly={
                                                                bisherigeStammdatenWerteGesperrt &&
                                                                hatStammdatenWochenstunden
                                                            }
                                                            onChange={(e) => {
                                                                if (
                                                                    bisherigeStammdatenWerteGesperrt &&
                                                                    hatStammdatenWochenstunden
                                                                ) {
                                                                    return;
                                                                }
                                                                wochenstundenAltManuellRef.current = true;
                                                                setAlteArbeitszeitStd(e.target.value);
                                                                clearFieldError('alte_arbeitszeit_std');
                                                            }}
                                                            placeholder="z.B. 38"
                                                            className={getInputClass(
                                                                bisherigeStammdatenWerteGesperrt &&
                                                                    hatStammdatenWochenstunden
                                                                    ? READONLY_INPUT_CLASS
                                                                    : INPUT_CLASS,
                                                                'alte_arbeitszeit_std'
                                                            )}
                                                        />
                                                        {renderFieldError('alte_arbeitszeit_std')}
                                                    </div>
                                                    <div>
                                                        <label className={LABEL_CLASS}>
                                                            {aenderungsBereiche.arbeitszeit
                                                                ? 'Neue Wochenstunden (aus Tagesplan)'
                                                                : 'Neue Wochenstunden'}
                                                        </label>
                                                        {aenderungsBereiche.arbeitszeit ? (
                                                            <>
                                                                <div
                                                                    className={`${READONLY_INPUT_CLASS} flex items-center font-semibold tabular-nums ${
                                                                        berechneteNeuWochenstunden > 0
                                                                            ? 'text-pe-accent-text dark:text-[#E6C299]'
                                                                            : ''
                                                                    }`}
                                                                >
                                                                    {berechneteNeuWochenstunden > 0
                                                                        ? `${formatStdForDisplay(berechneteNeuWochenstunden)} Std./Woche`
                                                                        : 'Tagesplan unten ausfüllen…'}
                                                                </div>
                                                                {renderFieldError('neue_arbeitszeit_std')}
                                                                {renderFieldError('neuer_arbeitsplan')}
                                                            </>
                                                        ) : (
                                                            <>
                                                                <input
                                                                    type="number"
                                                                    min={0.1}
                                                                    step={0.5}
                                                                    value={neueArbeitszeitStd}
                                                                    onChange={(e) => {
                                                                        wochenstundenNeuManuellRef.current = true;
                                                                        setNeueArbeitszeitStd(e.target.value);
                                                                        clearFieldError('neue_arbeitszeit_std');
                                                                    }}
                                                                    placeholder="z.B. 32"
                                                                    className={getInputClass(
                                                                        INPUT_CLASS,
                                                                        'neue_arbeitszeit_std'
                                                                    )}
                                                                />
                                                                {renderFieldError('neue_arbeitszeit_std')}
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        {aenderungsBereiche.arbeitszeit && (
                                            <div className="space-y-4">
                                                <ArbeitszeitPlanEditor
                                                    title="Bisheriger Arbeitsplan (optional)"
                                                    plan={alterArbeitsplan}
                                                    onChange={setAlterArbeitsplan}
                                                    isFieldInvalid={isFieldInvalid}
                                                    onClearError={clearFieldError}
                                                    emptyHint="Optional – über „Tag hinzufügen“ eintragen."
                                                    wochensatz={wochensatz}
                                                    targetWochenstunden={
                                                        altWochenstundenNum > 0 ? altWochenstundenNum : null
                                                    }
                                                />
                                                <ArbeitszeitPlanEditor
                                                    title="Neuer Arbeitsplan (Wochentag, Von–Bis, Pause)"
                                                    plan={neuerArbeitsplan}
                                                    onChange={setNeuerArbeitsplan}
                                                    required
                                                    isFieldInvalid={isFieldInvalid}
                                                    onClearError={clearFieldError}
                                                    wochensatz={wochensatz}
                                                />
                                                {renderFieldError('neuer_arbeitsplan')}
                                            </div>
                                        )}
                                        {aenderungsBereiche.verguetung && (
                                            <div className="space-y-4">
                                                <p className="text-xs text-pe-muted dark:text-slate-400">
                                                    Bruttolohn oder Stundenlohn eingeben – der jeweils andere Wert wird automatisch über Wochenstunden × Wochensatz ({wochensatz.toFixed(2).replace('.', ',')}) berechnet.
                                                </p>
                                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                                    <div
                                                        className={`space-y-3 p-4 rounded-lg border ${
                                                            bisherigeStammdatenWerteGesperrt && hatStammdatenVerguetung
                                                                ? 'border-amber-500/35 bg-amber-500/[0.04]'
                                                                : 'border-pe-border dark:border-slate-700'
                                                        }`}
                                                    >
                                                        <div className="flex items-start justify-between gap-3">
                                                            <div className="min-w-0">
                                                                <p className="text-sm font-semibold text-pe-text dark:text-white">
                                                                    Bisherige Vergütung
                                                                </p>
                                                                {selectedStammdatenMitarbeiter ? (
                                                                    <p className="text-[10px] text-pe-muted dark:text-slate-500 mt-0.5">
                                                                        {bisherigeStammdatenWerteGesperrt
                                                                            ? 'Aus System / Datenblatt — geschützt'
                                                                            : 'Freigegeben zur Korrektur — „Stammdaten“ stellt den Originalwert wieder her'}
                                                                    </p>
                                                                ) : null}
                                                            </div>
                                                            <div className="flex items-center gap-2 shrink-0">
                                                                {renderBisherigeStammdatenUnlockButton()}
                                                                {renderStammdatenResetButton()}
                                                            </div>
                                                        </div>
                                                        {bisherigeStammdatenWerteGesperrt && hatStammdatenVerguetung && (
                                                            <p className="text-[11px] leading-relaxed rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-amber-800 dark:text-amber-200">
                                                                Stopp: Das ist der <strong>Grundlohn aus dem System</strong>.
                                                                Er wird nicht einfach überschrieben — nur nach Bestätigung über
                                                                „Ändern…“.
                                                            </p>
                                                        )}
                                                        {renderGehaltModeToggle(
                                                            altGehaltMode,
                                                            setAltGehaltMode,
                                                            'Eingabeart',
                                                            bisherigeStammdatenWerteGesperrt && hatStammdatenVerguetung
                                                        )}
                                                        {altGehaltMode === 'brutto' ? (
                                                            <>
                                                                <div>
                                                                    <label className={LABEL_CLASS}>Bruttolohn / Monat (€)</label>
                                                                    <input
                                                                        type="text"
                                                                        value={alteVerguetung}
                                                                        readOnly={
                                                                            bisherigeStammdatenWerteGesperrt &&
                                                                            hatStammdatenVerguetung
                                                                        }
                                                                        onChange={(e) => {
                                                                            if (
                                                                                bisherigeStammdatenWerteGesperrt &&
                                                                                hatStammdatenVerguetung
                                                                            ) {
                                                                                return;
                                                                            }
                                                                            setAlteVerguetung(e.target.value);
                                                                            clearFieldError('alte_verguetung');
                                                                        }}
                                                                        placeholder="z.B. 3.000"
                                                                        className={getInputClass(
                                                                            bisherigeStammdatenWerteGesperrt &&
                                                                                hatStammdatenVerguetung
                                                                                ? READONLY_INPUT_CLASS
                                                                                : INPUT_CLASS,
                                                                            'alte_verguetung'
                                                                        )}
                                                                    />
                                                                    {renderFieldError('alte_verguetung')}
                                                                </div>
                                                                <div>
                                                                    <label className={LABEL_CLASS}>Berechneter Stundenlohn</label>
                                                                    <input type="text" readOnly value={altEffektivStundenlohn > 0 ? `${altEffektivStundenlohn.toFixed(2).replace('.', ',')} € / Std.` : '—'} className={READONLY_INPUT_CLASS} />
                                                                </div>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <div>
                                                                    <label className={LABEL_CLASS}>Stundenlohn (€ brutto / Std.)</label>
                                                                    <input
                                                                        type="text"
                                                                        value={alteStundenlohn}
                                                                        readOnly={
                                                                            bisherigeStammdatenWerteGesperrt &&
                                                                            hatStammdatenVerguetung
                                                                        }
                                                                        onChange={(e) => {
                                                                            if (
                                                                                bisherigeStammdatenWerteGesperrt &&
                                                                                hatStammdatenVerguetung
                                                                            ) {
                                                                                return;
                                                                            }
                                                                            setAlteStundenlohn(e.target.value);
                                                                            clearFieldError('alte_stundenlohn');
                                                                        }}
                                                                        placeholder="z.B. 15,50"
                                                                        className={getInputClass(
                                                                            bisherigeStammdatenWerteGesperrt &&
                                                                                hatStammdatenVerguetung
                                                                                ? READONLY_INPUT_CLASS
                                                                                : INPUT_CLASS,
                                                                            'alte_stundenlohn'
                                                                        )}
                                                                    />
                                                                    {renderFieldError('alte_stundenlohn')}
                                                                </div>
                                                                <div>
                                                                    <label className={LABEL_CLASS}>Berechneter Bruttolohn / Monat</label>
                                                                    <input type="text" readOnly value={altEffektivBrutto > 0 ? `${altEffektivBrutto.toFixed(2).replace('.', ',')} €` : '—'} className={READONLY_INPUT_CLASS} />
                                                                </div>
                                                            </>
                                                        )}
                                                    </div>
                                                    <div className="space-y-3 p-4 rounded-lg border border-pe-border dark:border-slate-700">
                                                        <p className="text-sm font-semibold text-pe-text dark:text-white">Neue Vergütung</p>
                                                        {renderGehaltModeToggle(neuGehaltMode, setNeuGehaltMode, 'Eingabeart')}
                                                        {neuGehaltMode === 'brutto' ? (
                                                            <>
                                                                <div>
                                                                    <label className={LABEL_CLASS}>Bruttolohn / Monat (€)</label>
                                                                    <input type="text" value={neueVerguetung} onChange={(e) => { setNeueVerguetung(e.target.value); clearFieldError('neue_verguetung'); }} placeholder="z.B. 3.000" className={getInputClass(INPUT_CLASS, 'neue_verguetung')} />
                                                                    {renderFieldError('neue_verguetung')}
                                                                </div>
                                                                <div>
                                                                    <label className={LABEL_CLASS}>Berechneter Stundenlohn</label>
                                                                    <input type="text" readOnly value={neuEffektivStundenlohn > 0 ? `${neuEffektivStundenlohn.toFixed(2).replace('.', ',')} € / Std.` : '—'} className={READONLY_INPUT_CLASS} />
                                                                </div>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <div>
                                                                    <label className={LABEL_CLASS}>Stundenlohn (€ brutto / Std.)</label>
                                                                    <input type="text" value={neueStundenlohn} onChange={(e) => { setNeueStundenlohn(e.target.value); clearFieldError('neue_stundenlohn'); }} placeholder="z.B. 15,50" className={getInputClass(INPUT_CLASS, 'neue_stundenlohn')} />
                                                                    {renderFieldError('neue_stundenlohn')}
                                                                </div>
                                                                <div>
                                                                    <label className={LABEL_CLASS}>Berechneter Bruttolohn / Monat</label>
                                                                    <input type="text" readOnly value={neuEffektivBrutto > 0 ? `${neuEffektivBrutto.toFixed(2).replace('.', ',')} €` : '—'} className={READONLY_INPUT_CLASS} />
                                                                </div>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        {aenderungsBereiche.filiale && (
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                <div>
                                                    <label className={LABEL_CLASS}>Bisheriger Salon / Filiale</label>
                                                    <input
                                                        type="text"
                                                        value={bisherigeFiliale}
                                                        onChange={(e) => {
                                                            setBisherigeFiliale(e.target.value);
                                                            clearFieldError('bisherige_filiale');
                                                        }}
                                                        className={getInputClass(INPUT_CLASS, 'bisherige_filiale')}
                                                    />
                                                    {renderFieldError('bisherige_filiale')}
                                                </div>
                                                <div>
                                                    <label className={LABEL_CLASS}>Neuer Salon / Filiale</label>
                                                    <input
                                                        type="text"
                                                        value={neueFiliale}
                                                        onChange={(e) => {
                                                            setNeueFiliale(e.target.value);
                                                            clearFieldError('neue_filiale');
                                                        }}
                                                        className={getInputClass(INPUT_CLASS, 'neue_filiale')}
                                                    />
                                                    {renderFieldError('neue_filiale')}
                                                </div>
                                            </div>
                                        )}
                                        {aenderungsBereiche.sonstiges && (
                                            <div>
                                                <label className={LABEL_CLASS}>Konkrete Beschreibung der Neuregelung</label>
                                                <textarea
                                                    value={sonstigesFreitext}
                                                    onChange={(e) => {
                                                        setSonstigesFreitext(e.target.value);
                                                        clearFieldError('sonstiges_freitext');
                                                        clearFieldError('aenderungs_bereiche');
                                                    }}
                                                    rows={3}
                                                    className={getInputClass(`${INPUT_CLASS} resize-y min-h-[80px]`, 'sonstiges_freitext')}
                                                />
                                                {renderFieldError('sonstiges_freitext')}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <SondervereinbarungenField
                                    value={sondervereinbarungen}
                                    onChange={setSondervereinbarungen}
                                    dokumentKontext="brief"
                                />
                            </div>
                        )}

                        {currentStep === 2 && (
                            <div className="w-full min-h-0 pr-2 mb-6">
                                {renderContractDocument()}
                            </div>
                        )}
                    </div>

                    {currentStep === 2 && (
                        <PePapersDisclaimerGate
                            accepted={disclaimerAccepted}
                            onAcceptedChange={setDisclaimerAccepted}
                        />
                    )}

                    <div className="px-6 py-4 border-t border-pe-border dark:border-[#222] shrink-0 print:hidden">
                        <div className="flex flex-wrap gap-3">
                            {currentStep > 1 && (
                                <button
                                    type="button"
                                    onClick={handleZurueck}
                                    className="flex-1 min-w-[120px] px-4 py-3 font-bold rounded-xl text-xs uppercase tracking-widest border bg-transparent hover:bg-pe-hover dark:hover:bg-[#222] border-pe-border-strong dark:border-[#444] text-pe-muted dark:text-gray-300 transition-colors"
                                >
                                    Zurück
                                </button>
                            )}

                            {currentStep === 1 ? (
                                <>
                                    <button
                                        type="button"
                                        onClick={() => void handleArchive(true)}
                                        disabled={isSaving}
                                        className="flex-1 min-w-[120px] px-4 py-3 font-bold rounded-xl text-xs uppercase tracking-widest border bg-transparent hover:bg-pe-hover dark:hover:bg-[#222] border-pe-border-strong dark:border-[#444] text-pe-muted dark:text-gray-300 transition-colors disabled:opacity-50"
                                    >
                                        {isSaving && archiveFeedback !== 'draft' ? 'Speichert...' : 'Später fortsetzen'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleWeiter}
                                        className="flex-1 min-w-[120px] px-4 py-3 font-bold rounded-xl text-xs uppercase tracking-widest bg-[#E6C299] hover:bg-[#d9b183] dark:hover:bg-white text-black transition-colors"
                                    >
                                        Dokument erstellen
                                    </button>
                                </>
                            ) : (
                                <>
                                    <button
                                        type="button"
                                        onClick={() => void handleArchive(true)}
                                        disabled={isSaving}
                                        className="flex-1 min-w-[140px] px-4 py-3 font-bold rounded-xl text-xs uppercase tracking-widest border bg-transparent hover:bg-pe-hover dark:hover:bg-[#222] border-pe-border-strong dark:border-[#444] text-pe-muted dark:text-gray-300 transition-colors disabled:opacity-50"
                                    >
                                        {archiveFeedback === 'draft' ? 'Gespeichert! ✓' : 'Später fortsetzen'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => void handleArchive(false)}
                                        disabled={isActionLocked || isSaving}
                                        className={getActionButtonClass(
                                            'flex-1 min-w-[140px] px-4 py-3 font-bold rounded-xl text-xs uppercase tracking-widest border bg-pe-card dark:bg-[#15171e] hover:bg-pe-hover dark:hover:bg-[#1a1d24] border-[#E6C299]/40 text-pe-accent-text dark:text-[#E6C299] transition-colors'
                                        )}
                                    >
                                        {isSaving && archiveFeedback !== 'draft'
                                            ? 'Speichert...'
                                            : archiveFeedback === 'success'
                                            ? 'Gespeichert! ✓'
                                            : 'Im Archiv speichern'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => void handleDownloadPdf()}
                                        disabled={isActionLocked || isPdfDownloading}
                                        className={getActionButtonClass(
                                            'flex-1 min-w-[140px] px-4 py-3 font-bold rounded-xl text-xs uppercase tracking-widest bg-gradient-to-b from-[#E6C299] to-[#b48a56] hover:from-[#f0d3b3] hover:to-[#cfa674] text-black transition-colors'
                                        )}
                                    >
                                        {isPdfDownloading ? 'PDF wird erstellt...' : 'Als PDF herunterladen'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handlePrintIsolated}
                                        disabled={isActionLocked || isPdfDownloading}
                                        className={getActionButtonClass(
                                            'flex-1 min-w-[140px] px-4 py-3 font-bold rounded-xl text-xs uppercase tracking-widest border bg-transparent hover:bg-pe-hover dark:hover:bg-[#222] border-[#E6C299]/40 text-pe-accent-text dark:text-[#E6C299] transition-colors'
                                        )}
                                    >
                                        Drucken
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>,
        document.body
    );
};

export default VertragsAnpassungKombiModal;
